/**
 * Three.js scene setup and rendering utilities.
 * Astroneer-inspired soft lighting and starfield.
 */

import { rockSeatHalf } from '../core/propSeat';
import { drives } from '../core/body';
import { markedParts } from '../core/parts';
import { applyTopoOverlay } from './topoOverlay';
import { applyGroundLift } from './groundLift';
import * as THREE from 'three';
import { createStarVisual, updateStarVisual, type StarVisual } from './starModel';
import { createCloudLayer, updateCloudLayer, disposeCloudLayer, type CloudLayer } from './cloudLayer';
import { surfacePropScale } from '../core/propScale';
import { seatGroup, groundRotationWithin, modelExtents, propSeatingOn, seatOnPlinth, reseatPlinth, type PropSeat, type PlinthSeat, type FineGround, seatQuaternion } from './groundSeat';
import { TREE_TRUNK_SIZE, ANTENNA_POST_PERSON_U, REFERENCE } from '../constants';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import type { Pass } from 'three/examples/jsm/postprocessing/Pass.js';
import type { GameState, PlanetState, PlayerState, StructureState, HazardState, HazardType, PlantedFlagState, RuinState, FloraState, RockState, EnvironmentalEffectState, Vec3, FogPlanetState, WeaponKey, FloraType, EnvironmentalEffectType, PlanetArchetype } from '../types';
import { TEAMS, FOG_DARK_COLOR, ARCHETYPE_GLOW, ROCK_MODE_SCALES, TREE_DRAW_SCALE, FLORA_DRAW_SCALE, NORMAL_TREE_PERSONS, NORMAL_ROCK_PERSONS } from '../constants';
import { fogStalenessDim } from '../core/fog';
import { getSurfaceRadiusRaw, getSurfaceRadius } from '../core/surface';
import { ammoCaseModel } from './roleLooks';   // 69.45: the support's case set down
import { bakeStaticParts, disposeObjectTree, syncInstancedGroups } from './meshBake';
import { computePlanetVertexColors } from '../core/planetColors';
import { buildPlayerModel, bakePlayerModel, createAnimContext, setFlagTexture, BUILD_SWING_DURATION, type PlayerModel, type AnimContext, type TeamColors } from './playerModel';
import { getFlagTexture } from './flagTextures';
import { buildTeamStructure } from './buildingModels';
import { buildLayoutLook, buildLayoutRuin, buildFallingLook, layoutLookExtent, settlementLook, type FallingLook } from './moduleLook';
import type { BuildingLayout } from '../types/modules';
import { plinthHalfPeople } from '../core/stands';   // (209's footing follow-up)
import { teamLayout, BASE_TEMPLATES } from '../core/modules';
import { unitPaletteFor, type UnitPalette } from './unitPalette';
import { buildTowerModel, bakeTowerModel } from './towerModel';
import { buildRelayModel, bakeRelayModel, setRelayLink, type RelayForm, type RelayLink } from './relayModel';
import { buildFootholdLook, bakeFootholdLook, dressRelayD, setSupplyLoad, footholdLights, type FootholdForm } from './footholdLooks';
import { buildAmmoDump, showAmmoDump, type AmmoDump } from './ammoBoxes';
import { PALLET } from '../constants/logistics';
import { isLinked } from '../core/web';
import { withHqRoot, setHqRootLink } from './hqRoot';
import { ruinSmokeAt, HqSmoke, HQ_FALL } from './hqFallLook';
import { buildEmplacementModel, bakeEmplacementModel } from './emplacementModel';
import { buildProjectileModel, PROJECTILE_SCALE } from './projectileModels';
import { buildRuinModel, buildClaimSign, claimSignKey, claimSignState } from './ruinModels';
import { buildFloraModel, emptyAnimData, type FloraAnimData } from './floraModels';
import { buildTree, buildRock, BIOME_COLORS, BIOME_GROUND, GROUND_TUFT_SCALE, TREE_SPECIES, type TreeSpecies, type RockKind } from './biomeKit';
import { treeProtoSeed } from './treeStands';
import { rockVariant, rockYaw } from '../core/treeDraws';
import { buildHazardModel } from './hazardModels';
import { SeededRandom } from '../core/random';
import { buildRockModel } from './rockModels';
import { buildEnvironmentalEffect, type EffectAnimData } from './environmentModels';
import { buildBeaconPost } from './beaconModels';
import { damageModel, DAMAGE_FORM } from './damageForms';
import { buildPointDefence, bakePointDefence, pdAnimator, buildGenerator, type PdForm, type PdState } from './pointDefenceLooks';   // 69.9
import { isSite, sitePowered } from '../core/pointDefence';   // 69.50 step 2b: a site dark while its generator is down
import { buildTowedGun, poseTowedGun } from './towedGun';   // 69.13 (a)
import { buildHelipad } from './helipadLook';   // 69.74 step 2b
import { BODIES } from '../constants/body';
import { STAND_TOPS, PAD_OVERHANG } from '../constants/action';

// =============================================================================
// SCENE OBJECTS
// =============================================================================

import type { EngineFlame } from './effects';
import { orientAlongDirection } from './effects';
import { drawSkippingHidden } from './hiddenWalk';

export interface StructureMesh {
  group: THREE.Group;       // container positioned on planet surface
  intactModel: THREE.Group; // healthy building (child of group)
  ruinedModel: THREE.Group; // destroyed building (child of group, initially hidden)
  /** A part that turns while the structure stands (the tower's radar head); swept by sweepStructureHeads. */
  spin?: THREE.Object3D;
  /** 67.73 step 2: the fog's word on it (the host's visibility pass); the far field shows the group only while also full. */
  fogShown?: boolean;
  /** The pad it stands on (groundSeat.ts); absent when placed the old way (radial, base on the centre point). */
  plinth?: PlinthSeat;
  /** Rising out of the ground after a build (`startStructureRise`): seconds so far and in all, the model's own scale. */
  rise?: { t: number; dur: number; base: THREE.Vector3 };
  /**
   * 63.4 step 8: the damaged form (lane E's `damageModel` pass on a fresh build), made the first time the structure falls
   * to STRUCTURE_DAMAGED_AT of its health; and how to make it. Its turning part, if any, replaces `spin` while shown.
   */
  damagedModel?: THREE.Group;
  makeDamaged?: () => { root: THREE.Group; spin?: THREE.Object3D };
  damagedSpin?: THREE.Object3D;
  /**
   * 63.4 step 7: the form with its detaching part gone (the tower headless, the emplacement shieldless — the piece itself
   * drawn as debris where the sim laid it), made the first time that part's health reaches 0 under partial destruction;
   * shown over the whole and the damaged forms while the structure stands.
   */
  partLostModel?: THREE.Group;
  /** 63.14 step 4: a modular building drawn by its modules' own healths (core/moduleDamage.ts) — made again when `modules` or `rubble` change (showStructureState); the arrays last seen. */
  makeModules?: (s: Pick<StructureState, 'modules' | 'rubble'> & Partial<Pick<StructureState, 'shut'>>) => THREE.Group;
  modulesSeen?: unknown;
  rubbleSeen?: unknown;
  /** 63.16 (b2): the `shut` list the look was last built from (a gate's leaves follow it). */
  shutSeen?: unknown;
  /** 72.3 step 3: a collapse's falling modules drawn (moduleLook.ts buildFallingLook) — made again when its `falls` change, posed every frame by the sim's tick; the list last seen. */
  makeFalls?: (falls: NonNullable<StructureState['collapse']>['falls']) => FallingLook;
  falls?: FallingLook;
  fallsSeen?: unknown;
  makePartLost?: () => { root: THREE.Group };
  /**
   * 69.2: each built form's web light (a relay's, the FOB stand-in's mast), set by `lightWeb` every frame from the state —
   * a form made later (the damaged one) registers its own, so it takes the state the frame it appears.
   */
  lights?: Array<(link: RelayLink, t: number) => void>;
  /** 69.5e: the HQ's smoke — from its breaches (the damaged form) and its ruin — stepped while its form shows (`stepStructureSmoke`). */
  smoke?: HqSmoke[];
  /**
   * 67.62: a supply dump's load — its stock's share of what it held when drawn (`full`, `supplyStock`), in its crate
   * layers' steps; each standing form registers its setter (footholdLooks setSupplyLoad), and `syncStructureMeshes` calls
   * them only when the step changes (`shown`; NaN: a form just made, which takes the step at once).
   */
  load?: { full: number; shown: number; sets: Array<(share: number) => void> };
  /**
   * 69.34 (DECISIONS 251): a dump's rounds by type (with GameConfig.munitionsSupply: a base crate's, a FOB's) drawn beside it
   * as typed crates in mixed stacks (rendering/ammoBoxes.ts), laid out again by `syncStructureMeshes` when its counts change —
   * the stacks shrink as the rearm takes them; hidden with the structure wrecked. Made with the structure when it holds rounds.
   */
  ammo?: AmmoDump;
  /** 69.9: each standing form's moving parts driven from the state every frame (a point-defence mount's slew, barrels and radar; `aimStructures`) — a form made later registers its own. */
  aims?: Array<(s: PdState, dt: number) => void>;
}

/** 67.62: everything a supply structure holds, summed (the load's measure: what is left of what it was dropped with). */
export function supplyStock(s: Partial<Pick<StructureState, 'supply'>>): number {
  const c = s.supply;
  return c ? c.bandages + c.magazines + (c.shells ?? 0) + (c.drones ?? 0) + (c.charges ?? 0) + (c.material ?? 0) : 0;
}
/** 67.62: the supply dump's crate layers, the load's steps. */
const SUPPLY_LOAD_STEPS = 3;
/** 69.34: a dump's stacks side by side in a row (its rows going away from the structure). */
const AMMO_DUMP_PER_ROW = 4;

/** 63.4 step 8: a structure at or under this share of its body's health shows its damaged form (as a tree does, TREE_FORM.damagedAt). */
export const STRUCTURE_DAMAGED_AT = 0.5;

export interface GhostMesh {
  group: THREE.Group;
  marker: THREE.Mesh;
  label: THREE.Sprite;
  playerId: number;
}

export interface StructureGhostMesh {
  group: THREE.Group;
  marker: THREE.Mesh;
  label: THREE.Sprite;
  structureId: number;
}

export interface ExposureMesh {
  group: THREE.Group;
  marker: THREE.Mesh;
  label: THREE.Sprite;
}

export interface PlantedFlagMesh {
  group: THREE.Group;
  fabric?: THREE.Mesh;
  beacon?: THREE.Mesh;
  ptLight?: THREE.PointLight;
}

export interface WormholeAnimData {
  diskGroup: THREE.Group;
  photonRingMat: THREE.MeshBasicMaterial;
  glowMat: THREE.MeshBasicMaterial;
  hotspots: THREE.Points;
  hotspotMat: THREE.PointsMaterial;
  hotspotData: Array<{ angle: number; dist: number; speed: number; phase: number; freq: number }>;
  infallPoints: THREE.Points;
  infallParticles: Array<{ angle: number; dist: number; yOffset: number; speed: number; infall: number; phase: number; freq: number }>;
  radius: number;
}

export interface PlanetMeshData {
  mesh: THREE.Mesh;
  glow?: THREE.Mesh;
  corona?: THREE.Mesh;
  star?: StarVisual;         // stars: everything lives in star.group
  clouds?: CloudLayer;       // planets with an atmosphere worth seeing
  ring?: THREE.Mesh;
  light?: THREE.PointLight;
  isStar?: boolean;
  isWormhole?: boolean;
  wormholeAnim?: WormholeAnimData;
  clickSphere?: THREE.Mesh;  // invisible sphere for reliable raycasting
}

export interface SceneObjects {
  renderer: THREE.WebGLRenderer;
  composer: EffectComposer | null;   // null when post-processing is disabled (?fx=0)
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  planetMeshes: Map<number, PlanetMeshData>;
  playerMeshes: Map<number, PlayerMesh>;
  structureMeshes: Map<number, StructureMesh>;
  projectileMesh: THREE.Group | null;
  trailLine: THREE.Line | null;
  previewLine: THREE.Line | null;
  aimIndicator: AimIndicator | null;
  engineFlame: EngineFlame | null;
  movementRing: MovementRing | null;
  ghostMeshes: GhostMesh[];
  structureGhostMeshes: StructureGhostMesh[];
  exposureMeshes: ExposureMesh[];
  plantedFlagMeshes: Map<number, PlantedFlagMesh>;
}

// Movement range ring
export interface MovementRing {
  ring: THREE.Line;
  dot: THREE.Mesh;
  costLabel: THREE.Sprite;
  costCanvas: HTMLCanvasElement;
  costTexture: THREE.CanvasTexture;
}

// Player character mesh — wrapper group for world positioning + model for animation
export interface PlayerMesh {
  group: THREE.Group;       // world positioning (position, quaternion)
  model: PlayerModel;       // character model (animation offsets applied here)
  animCtx: AnimContext;     // per-player animation state
}

// =============================================================================
// SCENE SETUP
// =============================================================================

export function createScene(): SceneObjects {
  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(
    55,
    window.innerWidth / window.innerHeight,
    0.1,
    500
  );
  camera.position.set(0, 30, 65);
  camera.lookAt(0, 0, 0);

  safeFlatNormals();   // (before any program compiles: a NaN pixel the bloom would spread)
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setClearColor(0x0b0b18, 1);
  // 69.98 (6) (DECISIONS 319): the production build skips three.js's check of each compiled program (getProgramInfoLog
  // straight after the link waits for the driver's compile: 80 % of a compiling frame in lane H's 69.94); the dev server
  // keeps it, with its errors in the console
  renderer.debug.checkShaderErrors = (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV !== false;
  // Filmic tone mapping: keeps the pastel palette but gives facets real contrast
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  document.body.insertBefore(renderer.domElement, document.body.firstChild);

  // Lighting — a clear key light with soft bounce. The old 0.7 ambient plus
  // hemisphere plus fill flattened every facet into the same tone.
  const ambient = new THREE.AmbientLight(0x8090a0, 0.35);
  scene.add(ambient);

  const sun = new THREE.DirectionalLight(0xffe8cc, 2.2);
  sun.position.set(40, 50, 30);
  scene.add(sun);

  const fill = new THREE.DirectionalLight(0x8888aa, 0.3);
  fill.position.set(-20, -30, -15);
  scene.add(fill);

  const hemi = new THREE.HemisphereLight(0xb0c0d0, 0x504840, 0.6);
  scene.add(hemi);

  createStarfield(scene);

  // Distant nebula sphere — wraps entire scene, no visible seams
  const nebulaGeo = new THREE.SphereGeometry(250, 32, 16);
  const nebulaMat = new THREE.MeshBasicMaterial({
    color: 0x1a1828,
    transparent: true,
    opacity: 0.15,
    side: THREE.BackSide,
  });
  const nebula = new THREE.Mesh(nebulaGeo, nebulaMat);
  nebula.userData.skyLayer = true;   // rendering/skyHaze.ts fades it inside an atmosphere
  scene.add(nebula);

  // Post-processing: bloom for emissive bits (flames, visors, stars,
  // wormholes), then tone-mapped output, then SMAA (MSAA is lost once
  // rendering goes through a composer).
  // Add ?fx=0 to the URL to compare against plain forward rendering.
  const fxEnabled = new URLSearchParams(window.location.search).get('fx') !== '0';
  let composer: EffectComposer | null = null;
  if (fxEnabled) {
    const w = window.innerWidth, h = window.innerHeight;
    composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    const bloom = new UnrealBloomPass(new THREE.Vector2(w, h), 0.45, 0.35, 0.8);
    composer.addPass(bloom);
    composer.addPass(new OutputPass());
    const SmaaCtor = SMAAPass as unknown as new (w: number, h: number) => Pass;
    composer.addPass(new SmaaCtor(w * renderer.getPixelRatio(), h * renderer.getPixelRatio()));
  }

  return {
    renderer,
    composer,
    scene,
    camera,
    planetMeshes: new Map(),
    playerMeshes: new Map(),
    structureMeshes: new Map(),
    projectileMesh: null,
    trailLine: null,
    previewLine: null,
    aimIndicator: null,
    engineFlame: null,
    movementRing: null,
    ghostMeshes: [],
    structureGhostMeshes: [],
    exposureMeshes: [],
    plantedFlagMeshes: new Map(),
  };
}

/** Three layers of stars: many faint, some medium, a few bright with warm/cool tints. */
function createStarfield(scene: THREE.Scene): void {
  const layers = [
    { count: 1800, size: 0.22, lo: 0.25, hi: 0.5 },
    { count: 420, size: 0.45, lo: 0.5, hi: 0.85 },
    { count: 70, size: 0.9, lo: 0.9, hi: 1.3 },
  ];
  for (const layer of layers) {
    const starGeo = new THREE.BufferGeometry();
    const positions = new Float32Array(layer.count * 3);
    const colors = new Float32Array(layer.count * 3);
    for (let i = 0; i < layer.count; i++) {
      const r = 160 + Math.random() * 80;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);
      const b = layer.lo + Math.random() * (layer.hi - layer.lo);
      const warm = Math.random();
      colors[i * 3] = b * (warm > 0.7 ? 1.0 : 0.88);
      colors[i * 3 + 1] = b * 0.92;
      colors[i * 3 + 2] = b * (warm > 0.7 ? 0.82 : 1.0);
    }
    starGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    starGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const starMat = new THREE.PointsMaterial({
      size: layer.size,
      vertexColors: true,
      sizeAttenuation: true,
      transparent: true,
      depthWrite: false,
    });
    const stars = new THREE.Points(starGeo, starMat);
    stars.userData.skyLayer = true;   // rendering/skyHaze.ts fades them inside an atmosphere
    scene.add(stars);
  }
}


// =============================================================================
// PLANET RENDERING
// =============================================================================

/** Additive Fresnel rim shader used for planet atmospheres. */
function createAtmosphereMaterial(color: THREE.Color, intensity: number): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: color },
      uIntensity: { value: intensity },
      uPower: { value: 3.2 },
    },
    vertexShader: `
      varying vec3 vN;
      varying vec3 vV;
      void main() {
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vN = normalize(normalMatrix * normal);
        vV = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `
      uniform vec3 uColor;
      uniform float uIntensity;
      uniform float uPower;
      varying vec3 vN;
      varying vec3 vV;
      void main() {
        float f = pow(1.0 - clamp(dot(normalize(vN), normalize(vV)), 0.0, 1.0), uPower);
        gl_FragColor = vec4(uColor * f * uIntensity, f * uIntensity);
      }`,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.FrontSide,
  });
}

export function createPlanetMesh(
  planet: PlanetState,
  scene: THREE.Scene
): PlanetMeshData {
  const colorA = new THREE.Color(planet.palette.a);
  const colorB = new THREE.Color(planet.palette.b);
  const colorC = planet.palette.c ? new THREE.Color(planet.palette.c) : colorA.clone().multiplyScalar(0.4);

  // Build geometry directly from planet's basePositions (don't use Three.js IcosahedronGeometry)
  const vertexCount = planet.heightmap.length;
  const positions = new Float32Array(vertexCount * 3);

  // Apply heightmap to base positions
  for (let i = 0; i < vertexCount; i++) {
    const bx = planet.basePositions[i * 3];
    const by = planet.basePositions[i * 3 + 1];
    const bz = planet.basePositions[i * 3 + 2];
    const len = Math.sqrt(bx * bx + by * by + bz * bz);
    const nx = bx / len, ny = by / len, nz = bz / len;
    positions[i * 3] = bx + nx * planet.heightmap[i];
    positions[i * 3 + 1] = by + ny * planet.heightmap[i];
    positions[i * 3 + 2] = bz + nz * planet.heightmap[i];
  }

  // Vertex colors — shared with crater/mound updates (core/planetColors)
  const colors = computePlanetVertexColors(planet);

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();

  const mat = new THREE.MeshLambertMaterial({
    vertexColors: true,
    flatShading: true,
  });

  applyTopoOverlay(mat);   // 68.17: the commander's topo overlay (off: a skipped term)
  applyGroundLift(mat);    // 67.50 (3): drawn at the fine relief's middle in an action battle (rendering/groundLift.ts)
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(planet.position.x, planet.position.y, planet.position.z);
  scene.add(mesh);

  // Atmosphere: Fresnel rim on a slightly larger sphere (bright at the limb,
  // invisible face-on) instead of the old flat 4%-opacity back-face shell.
  const glowGeo = new THREE.SphereGeometry(planet.radius * 1.07, 40, 28);
  const glowConfig = ARCHETYPE_GLOW[planet.archetype as PlanetArchetype];
  const avgColor = colorA.clone().lerp(colorB, 0.5);
  const glowColor = glowConfig ? new THREE.Color(glowConfig.color) : avgColor;
  const glowOpacity = glowConfig ? glowConfig.opacity : 0.04;
  const glowMat = createAtmosphereMaterial(glowColor, Math.min(0.9, Math.max(0.3, glowOpacity * 8)));
  const glow = new THREE.Mesh(glowGeo, glowMat);
  glow.position.set(planet.position.x, planet.position.y, planet.position.z);
  scene.add(glow);

  // Invisible click sphere for reliable raycasting (terrain mesh is too irregular for distant planets)
  const clickGeo = new THREE.SphereGeometry(planet.radius * 1.05, 16, 12);
  const clickMat = new THREE.MeshBasicMaterial({ visible: false });
  const clickSphere = new THREE.Mesh(clickGeo, clickMat);
  clickSphere.position.set(planet.position.x, planet.position.y, planet.position.z);
  scene.add(clickSphere);

  // Clouds (visual only; never raycast; archetypes without a profile stay clear)
  const clouds = createCloudLayer(planet) ?? undefined;
  if (clouds) scene.add(clouds.group);

  return { mesh, glow, clickSphere, clouds };
}

export function createStarMesh(
  planet: PlanetState,
  scene: THREE.Scene
): PlanetMeshData {
  // Shader surface + corona + prominences + light (rendering/starModel.ts)
  const star = createStarVisual(planet);
  scene.add(star.group);

  // Click sphere for stars too
  const clickGeo = new THREE.SphereGeometry(planet.radius * 1.05, 16, 12);
  const clickMat = new THREE.MeshBasicMaterial({ visible: false });
  const clickSphere = new THREE.Mesh(clickGeo, clickMat);
  clickSphere.position.set(planet.position.x, planet.position.y, planet.position.z);
  scene.add(clickSphere);

  return { mesh: star.surface, corona: star.corona, light: star.light, star, isStar: true, clickSphere };
}

export function updateStarCorona(meshData: PlanetMeshData, time: number, camera: THREE.Camera): void {
  if (meshData.star) updateStarVisual(meshData.star, time, camera);
}

/** Per frame: drift a planet's cloud layer. */
export function updatePlanetClouds(meshData: PlanetMeshData, dt: number, time: number): void {
  if (meshData.clouds) updateCloudLayer(meshData.clouds, dt, time);
}

export function disposePlanetClouds(scene: THREE.Scene, meshData: PlanetMeshData): void {
  if (meshData.clouds) { disposeCloudLayer(scene, meshData.clouds); meshData.clouds = undefined; }
}

export function createWormholeMesh(
  planet: PlanetState,
  scene: THREE.Scene
): PlanetMeshData {
  const r = planet.radius;
  const px = planet.position.x, py = planet.position.y, pz = planet.position.z;

  // Wormhole palette (consistent look regardless of planet palette)
  const PALETTE = {
    throat:     '#050510',
    photonRing: '#c8a060',
    diskInner:  '#d4a050',
    diskMid:    '#a06830',
    diskOuter:  '#4a2818',
    glow:       '#3a2010',
    particles:  '#e0b868',
  };

  // Container group for all wormhole elements
  const whGroup = new THREE.Group();
  whGroup.position.set(px, py, pz);

  // 1. Throat — dark void sphere
  const throatGeo = new THREE.SphereGeometry(r * 0.32, 16, 12);
  const throatMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(PALETTE.throat) });
  const mesh = new THREE.Mesh(throatGeo, throatMat);
  whGroup.add(mesh);

  // 2. Photon ring — bright thin ring at throat edge
  const photonRingGeo = new THREE.TorusGeometry(r * 0.36, r * 0.018, 8, 32);
  const photonRingMat = new THREE.MeshBasicMaterial({
    color: new THREE.Color(PALETTE.photonRing),
    transparent: true,
    opacity: 0.95,
  });
  const ring = new THREE.Mesh(photonRingGeo, photonRingMat);
  ring.rotation.x = Math.PI / 2;
  whGroup.add(ring);

  // 3. Accretion disk — 5 concentric tori grading from gold to dark red
  const diskGroup = new THREE.Group();
  const diskSegments = 5;
  const cInner = new THREE.Color(PALETTE.diskInner);
  const cMid = new THREE.Color(PALETTE.diskMid);
  const cOuter = new THREE.Color(PALETTE.diskOuter);

  for (let i = 0; i < diskSegments; i++) {
    const t = i / (diskSegments - 1);
    const innerR = r * (0.42 + t * 0.5);
    const thickness = r * (0.025 - t * 0.012);
    const color = new THREE.Color();
    if (t < 0.5) {
      color.lerpColors(cInner, cMid, t * 2);
    } else {
      color.lerpColors(cMid, cOuter, (t - 0.5) * 2);
    }
    const opacity = 0.7 - t * 0.45;
    const ringGeo = new THREE.TorusGeometry(innerR, thickness, 6, 32);
    const ringMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      side: THREE.DoubleSide,
    });
    const diskRing = new THREE.Mesh(ringGeo, ringMat);
    diskRing.rotation.x = Math.PI / 2;
    diskGroup.add(diskRing);
  }
  whGroup.add(diskGroup);

  // 4. Hotspot particles — bright spots orbiting in disk plane
  const hotspotCount = 25;
  const hotspotGeo = new THREE.BufferGeometry();
  const hotspotPositions = new Float32Array(hotspotCount * 3);
  const hotspotData: WormholeAnimData['hotspotData'] = [];

  for (let i = 0; i < hotspotCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const dist = r * (0.42 + Math.random() * 0.5);
    hotspotPositions[i * 3] = Math.cos(angle) * dist;
    hotspotPositions[i * 3 + 1] = (Math.random() - 0.5) * r * 0.03;
    hotspotPositions[i * 3 + 2] = Math.sin(angle) * dist;
    hotspotData.push({
      angle,
      dist,
      speed: 0.3 + (1.0 - (dist - r * 0.42) / (r * 0.5)) * 0.7,
      phase: Math.random() * Math.PI * 2,
      freq: 4 + Math.random() * 8,
    });
  }
  hotspotGeo.setAttribute('position', new THREE.BufferAttribute(hotspotPositions, 3));
  const hotspotMat = new THREE.PointsMaterial({
    color: new THREE.Color(PALETTE.diskInner),
    size: r * 0.04,
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const hotspots = new THREE.Points(hotspotGeo, hotspotMat);
  whGroup.add(hotspots);

  // 5. Infall particles — spiraling into the throat
  const infallCount = 15;
  const infallGeo = new THREE.BufferGeometry();
  const infallPositions = new Float32Array(infallCount * 3);
  const infallParticles: WormholeAnimData['infallParticles'] = [];

  for (let i = 0; i < infallCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const dist = r * (0.35 + Math.random() * 0.55);
    infallPositions[i * 3] = Math.cos(angle) * dist;
    infallPositions[i * 3 + 1] = (Math.random() - 0.5) * r * 0.1;
    infallPositions[i * 3 + 2] = Math.sin(angle) * dist;
    infallParticles.push({
      angle,
      dist,
      yOffset: infallPositions[i * 3 + 1],
      speed: 0.2 + Math.random() * 0.4,
      infall: 0.08 + Math.random() * 0.15,
      phase: Math.random() * Math.PI * 2,
      freq: 6 + Math.random() * 10,
    });
  }
  infallGeo.setAttribute('position', new THREE.BufferAttribute(infallPositions, 3));
  const infallMat = new THREE.PointsMaterial({
    color: new THREE.Color(PALETTE.particles),
    size: r * 0.035,
    transparent: true,
    opacity: 0.8,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const infallPoints = new THREE.Points(infallGeo, infallMat);
  whGroup.add(infallPoints);

  // 6. Glow shell — subtle warm halo
  const glowGeo = new THREE.SphereGeometry(r * 1.1, 16, 12);
  const glowMat = new THREE.MeshBasicMaterial({
    color: new THREE.Color(PALETTE.glow),
    transparent: true,
    opacity: 0.06,
    side: THREE.BackSide,
  });
  const glow = new THREE.Mesh(glowGeo, glowMat);
  whGroup.add(glow);

  // Warm center light
  const light = new THREE.PointLight(new THREE.Color(PALETTE.diskInner), 0.6, r * 8);
  whGroup.add(light);

  scene.add(whGroup);

  // Click sphere for wormhole raycasting
  const clickGeo = new THREE.SphereGeometry(planet.radius * 1.05, 16, 12);
  const clickMat = new THREE.MeshBasicMaterial({ visible: false });
  const clickSphere = new THREE.Mesh(clickGeo, clickMat);
  clickSphere.position.set(px, py, pz);
  scene.add(clickSphere);

  return {
    mesh: whGroup as unknown as THREE.Mesh, // group used as mesh slot for position tracking
    glow,
    ring,
    light,
    isWormhole: true,
    clickSphere,
    wormholeAnim: {
      diskGroup,
      photonRingMat,
      glowMat,
      hotspots,
      hotspotMat,
      hotspotData,
      infallPoints,
      infallParticles,
      radius: r,
    },
  };
}

export function updateWormholeEffect(meshData: PlanetMeshData, time: number, dt: number): void {
  const anim = meshData.wormholeAnim;
  if (!anim) return;

  const r = anim.radius;

  // Disk rotation — differential, inner faster than outer
  anim.diskGroup.rotation.y += 0.003;

  // Photon ring subtle brightness pulse
  anim.photonRingMat.opacity = 0.85 + Math.sin(time * 1.5) * 0.1;

  // Glow shell breathing
  if (meshData.glow) {
    const gs = 1.0 + Math.sin(time * 0.6) * 0.02;
    meshData.glow.scale.setScalar(gs);
    anim.glowMat.opacity = 0.05 + Math.sin(time * 0.9) * 0.015;
  }

  // Hotspot orbiting — each at Keplerian speed
  const hPos = anim.hotspots.geometry.attributes.position;
  for (let i = 0; i < anim.hotspotData.length; i++) {
    const h = anim.hotspotData[i];
    h.angle += h.speed * dt;
    hPos.array[i * 3] = Math.cos(h.angle) * h.dist;
    hPos.array[i * 3 + 2] = Math.sin(h.angle) * h.dist;
  }
  hPos.needsUpdate = true;
  anim.hotspotMat.opacity = 0.5 + Math.sin(time * 3) * 0.1;

  // Infall particles — spiral inward, reset at throat boundary
  const iPos = anim.infallPoints.geometry.attributes.position;
  for (let i = 0; i < anim.infallParticles.length; i++) {
    const p = anim.infallParticles[i];
    p.angle += p.speed * dt * (r * 0.5 / Math.max(p.dist, r * 0.1));
    p.dist -= p.infall * dt;
    p.yOffset *= 0.998;

    const twinkle = Math.sin(time * p.freq + p.phase);

    if (p.dist < r * 0.28) {
      // Reset — respawn at outer disk
      p.dist = r * (0.6 + Math.random() * 0.3);
      p.angle = Math.random() * Math.PI * 2;
      p.yOffset = (Math.random() - 0.5) * r * 0.1;
      p.speed = 0.2 + Math.random() * 0.4;
      p.infall = 0.08 + Math.random() * 0.15;
    }

    iPos.array[i * 3] = Math.cos(p.angle) * p.dist + twinkle * 0.01;
    iPos.array[i * 3 + 1] = p.yOffset;
    iPos.array[i * 3 + 2] = Math.sin(p.angle) * p.dist;
  }
  iPos.needsUpdate = true;

  // Light intensity subtle pulse
  if (meshData.light) {
    meshData.light.intensity = 0.5 + Math.sin(time * 1.2) * 0.15;
  }
}

/**
 * Push heightmap changes into the planet mesh. With `modifiedVertices` only
 * that index range is recomputed and re-uploaded to the GPU; the material is
 * flat-shaded (normals come from screen-space derivatives) so no normal
 * recompute is needed.
 */
export function updatePlanetMesh(
  planet: PlanetState,
  meshData: PlanetMeshData,
  modifiedVertices?: ArrayLike<number>
): void {
  // Skip stars and wormholes — they use static geometry
  if (meshData.isStar || meshData.isWormhole) return;

  const geo = meshData.mesh.geometry as THREE.BufferGeometry;
  const posAttr = geo.attributes.position as THREE.BufferAttribute;
  const posArr = posAttr.array as Float32Array;

  let start = 0, end = planet.heightmap.length; // [start, end)
  if (modifiedVertices && modifiedVertices.length > 0) {
    let lo = Infinity, hi = -Infinity;
    for (let k = 0; k < modifiedVertices.length; k++) {
      const v = modifiedVertices[k];
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    start = lo; end = hi + 1;
  }

  for (let i = start; i < end; i++) {
    const bx = planet.basePositions[i * 3];
    const by = planet.basePositions[i * 3 + 1];
    const bz = planet.basePositions[i * 3 + 2];
    const len = Math.sqrt(bx * bx + by * by + bz * bz);
    const nx = bx / len, ny = by / len, nz = bz / len;
    posArr[i * 3] = bx + nx * planet.heightmap[i];
    posArr[i * 3 + 1] = by + ny * planet.heightmap[i];
    posArr[i * 3 + 2] = bz + nz * planet.heightmap[i];
  }

  if (start > 0 || end < planet.heightmap.length) {
    posAttr.addUpdateRange(start * 3, (end - start) * 3);
  }
  posAttr.needsUpdate = true;
}

export function updatePlanetColors(
  planet: PlanetState,
  meshData: PlanetMeshData,
  craterColors: Float32Array
): void {
  if (meshData.isStar || meshData.isWormhole) return;
  const geo = meshData.mesh.geometry as THREE.BufferGeometry;
  const colorAttr = geo.attributes.color;
  const colors = colorAttr.array as Float32Array;
  colors.set(craterColors);
  colorAttr.needsUpdate = true;
}

// =============================================================================
// FOG OF WAR RENDERING
// =============================================================================

/**
 * Apply fog colors to a planet mesh based on per-player fog state.
 * Unrevealed vertices get dark color. Revealed use snapshot colors.
 */
export function applyFogColors(
  meshData: PlanetMeshData,
  fogPlanetState: FogPlanetState,
  currentTurn: number
): void {
  // Stars and wormholes always rendered at full brightness
  if (meshData.isStar || meshData.isWormhole) return;
  const geo = meshData.mesh.geometry as THREE.BufferGeometry;
  const colorAttr = geo.attributes.color;
  const colors = colorAttr.array as Float32Array;
  const vertexCount = fogPlanetState.revealedVertices.length;
  // Stale intel dims the whole planet by its most recent reveal. Per-vertex dimming made
  // the material bands crawl from turn to turn as different patches aged (37.2c).
  let latest = -1;
  for (let i = 0; i < vertexCount; i++) if (fogPlanetState.revealedVertices[i] && fogPlanetState.revealTurn[i] > latest) latest = fogPlanetState.revealTurn[i];
  const dimFactor = latest >= 0 ? fogStalenessDim(currentTurn - latest) : 1;

  for (let i = 0; i < vertexCount; i++) {
    if (!fogPlanetState.revealedVertices[i]) {
      // Unrevealed: dark
      colors[i * 3] = FOG_DARK_COLOR.r;
      colors[i * 3 + 1] = FOG_DARK_COLOR.g;
      colors[i * 3 + 2] = FOG_DARK_COLOR.b;
    } else {
      // Revealed: use snapshot colors
      colors[i * 3] = fogPlanetState.colorSnapshot[i * 3];
      colors[i * 3 + 1] = fogPlanetState.colorSnapshot[i * 3 + 1];
      colors[i * 3 + 2] = fogPlanetState.colorSnapshot[i * 3 + 2];

      // Stale reveals dim so old intel reads as old (core/fog.ts fogStalenessDim)
      if (dimFactor < 1) {
        colors[i * 3] *= dimFactor;
        colors[i * 3 + 1] *= dimFactor;
        colors[i * 3 + 2] *= dimFactor;
      }
    }
  }

  colorAttr.needsUpdate = true;
}

/**
 * Get the current vertex colors from a planet mesh (the "true" colors).
 * Used to snapshot into fog state.
 */
export function getPlanetMeshColors(
  meshData: PlanetMeshData
): Float32Array {
  if (meshData.isStar || meshData.isWormhole) return new Float32Array(0);
  const geo = meshData.mesh.geometry as THREE.BufferGeometry;
  const colorAttr = geo.attributes.color;
  return new Float32Array(colorAttr.array as Float32Array);
}

// =============================================================================
// GHOST MARKER RENDERING
// =============================================================================

// Marker readability (Phase 37.2b): markers are built from camera-facing
// sprites with a dark backing so they read against any terrain, sit on a pin
// above the surface, and are rescaled every frame to a roughly constant screen
// size (updateMarkerScreenScale) so zooming out never shrinks them away.

/** World-unit size the marker sprites are authored at; scaled per frame against this camera distance. */
export const MARKER_REFERENCE_DISTANCE = 35;
export const MARKER_MIN_SCALE = 0.7;
export const MARKER_MAX_SCALE = 3.0;

/** A rounded, dark-backed label on a canvas texture. */
function makeLabelSprite(text: string, color: string, px: number, opacity: number): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.font = `bold ${px}px 'JetBrains Mono', monospace`;
  const w = Math.min(248, ctx.measureText(text).width + 24);
  const x = (256 - w) / 2;
  ctx.fillStyle = 'rgba(8, 10, 16, 0.78)';
  ctx.beginPath();
  ctx.roundRect(x, 8, w, 48, 10);
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 128, 33);
  const texture = new THREE.CanvasTexture(canvas);
  const mat = new THREE.SpriteMaterial({ map: texture, transparent: true, opacity, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(2.4, 0.6, 1);
  sprite.renderOrder = 20;
  return sprite;
}

/** A diamond or hourglass icon with a dark disc behind it. */
function makeIconSprite(shape: 'diamond' | 'hourglass' | 'square', color: string, opacity: number): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = 'rgba(8, 10, 16, 0.7)';
  ctx.beginPath();
  ctx.arc(32, 32, 30, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = color;
  ctx.strokeStyle = 'rgba(255,255,255,0.85)';
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  if (shape === 'diamond') {
    ctx.moveTo(32, 10); ctx.lineTo(52, 32); ctx.lineTo(32, 54); ctx.lineTo(12, 32); ctx.closePath();
  } else if (shape === 'hourglass') {
    ctx.moveTo(14, 12); ctx.lineTo(50, 12); ctx.lineTo(32, 32); ctx.lineTo(50, 52); ctx.lineTo(14, 52); ctx.lineTo(32, 32); ctx.closePath();
  } else {
    ctx.rect(14, 14, 36, 36);
  }
  ctx.fill();
  ctx.stroke();
  const texture = new THREE.CanvasTexture(canvas);
  const mat = new THREE.SpriteMaterial({ map: texture, transparent: true, opacity, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(1.1, 1.1, 1);
  sprite.renderOrder = 20;
  return sprite;
}

/** A thin vertical pin from the surface point up to the icon. */
function makePin(color: string, height: number, opacity: number): THREE.Line {
  const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, height, 0)]);
  const mat = new THREE.LineBasicMaterial({ color: new THREE.Color(color), transparent: true, opacity, depthTest: false });
  const line = new THREE.Line(geo, mat);
  line.renderOrder = 19;
  return line;
}

/** Dispose everything a marker group owns. */
function disposeMarkerGroup(scene: THREE.Scene, group: THREE.Group): void {
  scene.remove(group);
  group.traverse((child) => {
    if (child instanceof THREE.Sprite) {
      (child.material as THREE.SpriteMaterial).map?.dispose();
      (child.material as THREE.Material).dispose();
    } else if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
      child.geometry.dispose();
      (child.material as THREE.Material).dispose();
    }
  });
}

/**
 * Rescale marker groups so they keep a roughly constant screen size: a marker
 * at MARKER_REFERENCE_DISTANCE is drawn at its authored size; further away it
 * grows (up to MARKER_MAX_SCALE), closer it shrinks (down to MARKER_MIN_SCALE).
 */
export function updateMarkerScreenScale(camera: THREE.Camera, groups: THREE.Group[]): void {
  for (const g of groups) {
    const dist = camera.position.distanceTo(g.position);
    const s = Math.max(MARKER_MIN_SCALE, Math.min(MARKER_MAX_SCALE, dist / MARKER_REFERENCE_DISTANCE));
    g.scale.setScalar(s);
  }
}

export function createGhostMesh(
  scene: THREE.Scene,
  position: Vec3,
  teamColor: string,
  turnsSince: number,
  playerId: number
): GhostMesh {
  const group = new THREE.Group();
  // Fresh sightings are bright; old ones fade but never below a readable floor
  const opacity = Math.max(0.45, 0.95 - turnsSince * 0.03);

  // Small physical diamond at the sighting point (keeps the GhostMesh contract)
  const markerGeo = new THREE.OctahedronGeometry(0.3, 0);
  const markerMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(teamColor), transparent: true, opacity: opacity * 0.8 });
  const marker = new THREE.Mesh(markerGeo, markerMat);
  group.add(marker);

  group.add(makePin(teamColor, 1.3, opacity * 0.7));
  const icon = makeIconSprite('diamond', teamColor, opacity);
  icon.position.y = 1.5;
  group.add(icon);

  const label = makeLabelSprite(turnsSince === 0 ? 'SEEN' : `SEEN T-${turnsSince}`, teamColor, 26, opacity);
  label.position.y = 2.45;
  group.add(label);

  group.position.set(position.x, position.y, position.z);
  scene.add(group);

  return { group, marker, label, playerId };
}

export function disposeGhostMeshes(scene: THREE.Scene, ghosts: GhostMesh[]): void {
  for (const g of ghosts) disposeMarkerGroup(scene, g.group);
}

// =============================================================================
// STRUCTURE GHOST MARKER RENDERING
// =============================================================================

export function createStructureGhostMesh(
  scene: THREE.Scene,
  position: Vec3,
  teamColor: string,
  turnsSince: number,
  structureId: number
): StructureGhostMesh {
  const group = new THREE.Group();
  const opacity = Math.max(0.4, 0.9 - turnsSince * 0.03);

  // Small cube — distinct from the diamond player ghosts
  const markerGeo = new THREE.BoxGeometry(0.3, 0.3, 0.3);
  const markerMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(teamColor), transparent: true, opacity: opacity * 0.8 });
  const marker = new THREE.Mesh(markerGeo, markerMat);
  group.add(marker);

  group.add(makePin(teamColor, 1.0, opacity * 0.6));
  const icon = makeIconSprite('square', teamColor, opacity * 0.9);
  icon.position.y = 1.2;
  icon.scale.set(0.8, 0.8, 1);
  group.add(icon);

  const label = makeLabelSprite(`BUILT T-${turnsSince}`, teamColor, 22, opacity * 0.9);
  label.position.y = 1.95;
  label.scale.set(2.0, 0.5, 1);
  group.add(label);

  group.position.set(position.x, position.y, position.z);
  scene.add(group);

  return { group, marker, label, structureId };
}

export function disposeStructureGhostMeshes(scene: THREE.Scene, ghosts: StructureGhostMesh[]): void {
  for (const g of ghosts) disposeMarkerGroup(scene, g.group);
}

// =============================================================================
// EXPOSURE MARKER RENDERING ("SPOTTED")
// =============================================================================

export const EXPOSURE_COLOR = '#ffb547';

export function createExposureMesh(
  scene: THREE.Scene,
  position: Vec3,
  turnsSince: number
): ExposureMesh {
  const group = new THREE.Group();
  // Just spotted: loud. Fades over EXPOSURE_MARKER_MAX_AGE turns but stays legible.
  const opacity = Math.max(0.5, 1.0 - turnsSince * 0.18);

  // Hourglass at the spot (keeps the ExposureMesh contract: marker = top cone)
  const coneGeo = new THREE.ConeGeometry(0.28, 0.45, 6);
  const markerMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(EXPOSURE_COLOR), transparent: true, opacity: opacity * 0.85 });
  const topCone = new THREE.Mesh(coneGeo, markerMat);
  topCone.position.y = 0.23;
  group.add(topCone);
  const bottomCone = new THREE.Mesh(coneGeo, markerMat);
  bottomCone.position.y = -0.23;
  bottomCone.rotation.x = Math.PI;
  group.add(bottomCone);

  group.add(makePin(EXPOSURE_COLOR, 1.4, opacity * 0.7));
  const icon = makeIconSprite('hourglass', EXPOSURE_COLOR, opacity);
  icon.position.y = 1.6;
  group.add(icon);

  const label = makeLabelSprite(turnsSince === 0 ? 'SPOTTED' : `SPOTTED T-${turnsSince}`, EXPOSURE_COLOR, 26, opacity);
  label.position.y = 2.55;
  group.add(label);

  group.position.set(position.x, position.y, position.z);
  scene.add(group);

  return { group, marker: topCone, label };
}

export function disposeExposureMeshes(scene: THREE.Scene, meshes: ExposureMesh[]): void {
  for (const m of meshes) disposeMarkerGroup(scene, m.group);
}

// =============================================================================
// PLAYER RENDERING — Astroneer-style composed primitives
// =============================================================================

const PLAYER_OFFSET = 0;  // Physics surface is now the exact mesh surface — no sink needed

export function createPlayerMesh(
  player: PlayerState,
  scene: THREE.Scene
): PlayerMesh {
  const team = TEAMS[player.teamIndex % TEAMS.length];
  const colors: TeamColors = {
    primary: team.primary,
    accent: team.accent,
    visor: team.visor,
    detail: team.detail,
    helmet: team.helmet,
    visorStyle: team.visorStyle,
  };

  const model = buildPlayerModel(colors);
  const animCtx = createAnimContext();

  // Apply team flag texture to backpack flag, then fold every static part into one mesh per
  // moving group / material kind (~11 draw calls per player instead of ~65)
  setFlagTexture(model, getFlagTexture(player.teamIndex));
  bakePlayerModel(model);

  // Wrapper group for world positioning (position + quaternion)
  // Model group is a child — animation offsets are in local space
  const group = new THREE.Group();
  model.group.rotation.y = Math.PI; // model is built facing +Z, flip to face -Z
  group.add(model.group);
  scene.add(group);

  return { group, model, animCtx };
}

export function updatePlayerMesh(
  player: PlayerState,
  playerMesh: PlayerMesh,
  worldPos: Vec3,
  surfaceNormal: Vec3
): void {
  const normal = new THREE.Vector3(surfaceNormal.x, surfaceNormal.y, surfaceNormal.z);

  // Position with offset above surface
  playerMesh.group.position.set(
    worldPos.x + normal.x * PLAYER_OFFSET,
    worldPos.y + normal.y * PLAYER_OFFSET,
    worldPos.z + normal.z * PLAYER_OFFSET
  );

  // Orient to stand on surface (feet toward planet center)
  const up = new THREE.Vector3(0, 1, 0);
  playerMesh.group.quaternion.setFromUnitVectors(up, normal);

  playerMesh.group.visible = player.alive;
}

export function disposePlayerMesh(scene: THREE.Scene, playerMesh: PlayerMesh): void {
  scene.remove(playerMesh.group);
  disposeObjectTree(playerMesh.group);
}

// =============================================================================
// STRUCTURE RENDERING
// =============================================================================

/** A burnt palette for a destroyed person-scale structure. */
/** A wreck's palette (scorched); 63.1d's follow-up: the 6×6's wreck takes it too (game/actionHost.ts). */
export const SCORCHED: UnitPalette = { body: '#4a4542', dark: '#24211f', flash: '#6a6460', metal: '#2b2e33' };
/** Ownerless structures (a sector garrison's, a harness prop) read as nobody's: neutral grey. */
const NEUTRAL_UNIT = unitPaletteFor({ primary: '#8a8f98' });

/**
 * A structure's mesh. Person-scale structures (`tower`, `emplacement` — WORKBOARD §10.2: a seat
 * or a two-person tower sizes from the person, never the planet) use the design-lane models at
 * `personHeight`; the rest are the team's planet-scaled buildings. `teamIndex` null = ownerless.
 */
export function createStructureMesh(
  structure: StructureState,
  teamIndex: number | null,
  worldPos: Vec3,
  surfaceNormal: Vec3,
  scene: THREE.Scene,
  planetRadius: number,
  personHeight = 1.1,
  opts: PropScaleOpts = {},
): StructureMesh {
  const t = TEAMS[(teamIndex ?? 0) % TEAMS.length];
  const unit = teamIndex === null ? NEUTRAL_UNIT : unitPaletteFor(t);

  let intactModel: THREE.Group;
  let ruinedModel: THREE.Group;
  let personScale = false;
  let kitExtent: { half: number; height: number } | undefined;   // 63.14 step 2: a layout's footprint for its plinth
  let padLayout: BuildingLayout | null = null;   // (209's footing follow-up: the layout the pad's half is read from, when modular)
  let spin: THREE.Object3D | undefined;
  let makeDamaged: StructureMesh['makeDamaged'];
  let makePartLost: StructureMesh['makePartLost'];
  let makeModules: StructureMesh['makeModules'];   // 63.14 step 4
  let makeFalls: StructureMesh['makeFalls'];   // 72.3 step 3
  const lights: NonNullable<StructureMesh['lights']> = [];   // 69.2: the forms' web lights (lightWeb)
  const aims: NonNullable<StructureMesh['aims']> = [];   // 69.9: the forms' moving parts (aimStructures)
  const smoke: HqSmoke[] = [];                                // 69.5e: the HQ's (stepStructureSmoke)
  let load: StructureMesh['load'];                            // 67.62: a supply dump's (syncStructureMeshes)
  // 63.7 (b): a planet-scale building sized by function (core/buildingScale.ts) is drawn at person scale, the same on every planet
  const byFunction = opts.buildings === 'function';

  if (structure.type === 'tower' || structure.type === 'emplacement') {
    personScale = true;
    const build = (p: UnitPalette): THREE.Group => {
      if (structure.type === 'tower') { const m = buildTowerModel(p, personHeight); bakeTowerModel(m); if (p === unit) spin = m.head; return m.root; }
      const m = buildEmplacementModel(p, personHeight); bakeEmplacementModel(m); return m.root;
    };
    intactModel = build(unit);
    ruinedModel = new THREE.Group();
    const wreck = build(SCORCHED);
    wreck.rotation.set(0.38, 0, 0.22);                                  // knocked over and sunk a little
    wreck.position.y = -0.15 * personHeight;
    ruinedModel.add(wreck);
    // 63.4 step 8: the damaged form — lane E's pass on a fresh build, before its bake (the tower keeps its radar head)
    makeDamaged = () => {
      if (structure.type === 'tower') { const m = buildTowerModel(unit, personHeight); damageModel(m.root, 7 + structure.id, DAMAGE_FORM.level, [m.head]); bakeTowerModel(m); return { root: m.root, spin: m.head }; }
      const m = buildEmplacementModel(unit, personHeight); damageModel(m.root, 7 + structure.id); bakeEmplacementModel(m); return { root: m.root };
    };
    // 63.4 step 7: its detaching part gone (the radar head, the shield), scorched as the damaged form is; the piece is debris
    makePartLost = () => {
      if (structure.type === 'tower') { const m = buildTowerModel(unit, personHeight, 0.6, { form: 'headless', fallen: false }); damageModel(m.root, 7 + structure.id, DAMAGE_FORM.level, [m.head]); bakeTowerModel(m); return { root: m.root }; }
      const m = buildEmplacementModel(unit, personHeight, 0.6, { form: 'shieldless' }); damageModel(m.root, 7 + structure.id); bakeEmplacementModel(m); return { root: m.root };
    };
  } else if (drives(structure) || structure.type === 'hmg' || structure.type === 'dropContainer' || structure.type === 'shipWreck') {   // (69.3b: the container left standing is drawn by the action host, turned to its yaw; the ship's crash: a wreck too, burning by its age)
    // X31: a bike moves every tick, so the action host draws it (rendering/vehicleModels.ts, placed per frame with its
    // rider); the structure's own mesh is empty — 63.1d: any body that drives, the transport too; 65.22: and the emplaced
    // HMG, whose gun swings with its gunner every frame (rendering/hmgModel.ts)
    personScale = true;
    intactModel = new THREE.Group();
    ruinedModel = new THREE.Group();
  } else if (structure.type === 'pointDefence') {
    // 69.9: the base's point-defence mount (rendering/pointDefenceLooks.ts) — the C-RAM on its trailer or the CIWS on its deck,
    // its turret, gun and radar driven from the state each frame (aimStructures); damaged, headless (its radar head shed) and
    // destroyed forms. One baked mesh per moving group: 6 draws whole, 4 headless or destroyed
    personScale = true;
    const variant = structure.pd?.variant ?? 'cram';
    const build = (p: UnitPalette, form: PdForm): THREE.Group => {
      const m = buildPointDefence(variant, p, personHeight, form);
      bakePointDefence(m);
      if (form !== 'destroyed') aims.push(pdAnimator(m));
      return m.root;
    };
    intactModel = build(unit, 'whole');
    ruinedModel = new THREE.Group();
    ruinedModel.add(build(SCORCHED, 'destroyed'));
    makeDamaged = () => ({ root: build(unit, 'damaged') });
    makePartLost = () => ({ root: build(unit, 'headless') });
  } else if (structure.type === 'helipad') {
    // 69.74 step 2b: the helipad (rendering/helipadLook.ts) — a slab with a ring and an H on each spot, baked (1 draw, 244 triangles);
    // destroyed, scorched. Its parked helicopters are the action host's. Metres at a person 1.8
    personScale = true;
    const build = (wrecked: boolean): THREE.Group => { const g = buildHelipad(wrecked); g.scale.setScalar(personHeight / 1.8); bakeStaticParts(g); return g; };
    intactModel = build(false);
    ruinedModel = new THREE.Group();
    ruinedModel.add(build(true));
  } else if (structure.type === 'towedGun') {
    // 69.13 (a): the towed gun deployed (rendering/towedGun.ts, the owner's "towed looks great") — its trails spread and the barrel
    // at the strike's lay, baked to its materials (2 draws); wrecked, scorched with its barrel dropped. Metres at a person 1.8
    personScale = true;
    const build = (p: UnitPalette, wrecked: boolean): THREE.Group => {
      const m = buildTowedGun(p);
      poseTowedGun(m, { deploy: 1, ...(wrecked ? { elevation: 0.12 } : {}) });
      m.root.remove(m.far);
      m.root.scale.setScalar(personHeight / 1.8);
      bakeStaticParts(m.root);
      return m.root;
    };
    intactModel = build(unit, false);
    ruinedModel = new THREE.Group();
    ruinedModel.add(build(SCORCHED, true));
  } else if (structure.type === 'generator') {
    // 69.50 step 2b: an air-defence site's generator (rendering/pointDefenceLooks.ts buildGenerator), baked to its materials; destroyed, scorched and knocked about
    personScale = true;
    const build = (p: UnitPalette, form: 'whole' | 'destroyed'): THREE.Group => { const g = buildGenerator(p, personHeight, form); bakeStaticParts(g); return g; };
    intactModel = build(unit, 'whole');
    ruinedModel = new THREE.Group();
    ruinedModel.add(build(SCORCHED, 'destroyed'));
  } else if (structure.type === 'ammoCase') {
    // 69.45: the support's ammunition case set down open (rendering/roleLooks.ts ammoCaseModel); broken, nothing (it is gone)
    personScale = true;
    const g = new THREE.Group(), m = ammoCaseModel();
    m.scale.setScalar(personHeight / 1.8); g.add(m); bakeStaticParts(g);
    intactModel = g;
    ruinedModel = new THREE.Group();
  } else if (structure.type === 'relay') {
    // 69.2f: the relay (lane E's look) — standing and lit; its damaged form (the top kinked over, an antenna hanging, a guy
    // line gone) and its wreck (the mast down, the head off, the case burst). The plant's stages, the web's light and the
    // head's turn to its link (setRelayStage / aimRelay) wait for the hosts that know them; the web's light is lightWeb's (69.2)
    personScale = true;
    // 67.62: in the D look (footholdLooks.ts dressRelayD: its blocks chamfered in place; the parts, the light and the hit shape as built)
    const build = (p: UnitPalette, form: RelayForm): THREE.Group => {
      const m = buildRelayModel(p, personHeight, { form }); dressRelayD(m); bakeRelayModel(m); setRelayLink(m, form === 'destroyed' ? 'off' : 'linked', 0);
      if (form !== 'destroyed') lights.push((link, t) => setRelayLink(m, link, t));
      return m.root;
    };
    intactModel = build(unit, 'whole');
    makeDamaged = () => ({ root: build(unit, 'damaged') });
    ruinedModel = build(SCORCHED, 'destroyed');
  } else if (structure.type === 'beacon') {
    // 125a: the beacon — a person-scale mast with the team's light; 67.62: in the D look (footholdLooks.ts: tripod, pole,
    // transceiver case, dish, strobe, inside today's hit shape), bent at its joint when struck, knocked flat when destroyed
    personScale = true;
    const build = (p: UnitPalette, form: FootholdForm): THREE.Group => { const m = buildFootholdLook('beacon', p, personHeight, form); bakeFootholdLook(m); return m.root; };
    intactModel = build(unit, 'whole');
    makeDamaged = () => ({ root: build(unit, 'damaged') });
    ruinedModel = build(SCORCHED, 'destroyed');
  } else if (structure.type === 'supply') {
    // 65.9: a supply crate; 67.62: in the D look (footholdLooks.ts: a strapped pallet of crated ammunition under a net,
    // jerrycans, the team's marker flag, inside today's hit shape); its top layer thrown off when struck, the crates thrown
    // round the pallet when wrecked. Its load follows its stock, a crate layer at a time (`load`; each layer baked on its own)
    personScale = true;
    const supplyLoad: NonNullable<StructureMesh['load']> = { full: supplyStock(structure), shown: NaN, sets: [] };
    load = supplyLoad;
    const build = (p: UnitPalette, form: FootholdForm): THREE.Group => {
      const m = buildFootholdLook('supply', p, personHeight, form);
      bakeFootholdLook(m, form !== 'destroyed');
      if (form !== 'destroyed') { supplyLoad.sets.push(k => setSupplyLoad(m, k)); supplyLoad.shown = NaN; }
      return m.root;
    };
    intactModel = build(unit, 'whole');
    makeDamaged = () => ({ root: build(unit, 'damaged') });
    ruinedModel = build(SCORCHED, 'destroyed');
  } else if (structure.type === 'fob' || ((structure.type === 'ecotourism' || structure.type === 'landingPad') && byFunction)) {
    // 67.62: in the D look (footholdLooks.ts), their hit shapes lane C's fits from these models (HITBOXES.fob, FOOTHOLD_HITBOXES):
    //   - the FOB (69.4's forward base): a Hesco U open to the rear, its stores under a net, the radio's mast at the back
    //     corner (its lamp the web's, as a relay's) and lamps on the front posts;
    //   - sized by function, the HQ (an operations centre in a Hesco square, its dish turning) and the landing pad (matting,
    //     its touchdown circle, edge lights blinking, a windsock);
    // walls broken, the fly torn, lamps out when struck; wrecked in the scorched palette. The amber lamps and the dishes run
    // by the frame with the web's lamps (footholdLights, lightWeb)
    personScale = true;
    const kind = structure.type === 'fob' ? 'fob' : structure.type === 'ecotourism' ? 'hq' : 'landingPad';
    // 69.5e kept on the HQ: its root mast beside it (the lamp the web's: lit standing, flickering damaged, dark fallen) and its
    // smoke — the struck form's from its torn fly (its own breaks show the blasts, so no holes are cut), the ruin's as before
    const seed = 7 + structure.id, puffs = (form: THREE.Group, src: ReturnType<typeof ruinSmokeAt>, each: number) => { const sm = new HqSmoke(src, each); form.add(sm.mesh); smoke.push(sm); };
    const build = (p: UnitPalette, form: FootholdForm): THREE.Group => {
      const m = buildFootholdLook(kind, p, personHeight, form);
      bakeFootholdLook(m);
      if (m.lamps.length || m.blink.length || m.dishes.length) lights.push(footholdLights(m, p));   // (a form made later: lightWeb sets it the frame it appears)
      if (kind !== 'hq') return m.root;
      const src = form === 'whole' ? [] : ruinSmokeAt(m.root, seed);   // (measured before the root mast stands beside it)
      const r = withHqRoot(m.root, p, form);
      if (form !== 'destroyed') lights.push((link, t) => setHqRootLink(r, link, t));
      if (src.length) puffs(m.root, src, form === 'destroyed' ? HQ_FALL.ruinPuffs : HQ_FALL.puffs);
      return m.root;
    };
    intactModel = build(unit, 'whole');
    makeDamaged = () => ({ root: build(unit, 'damaged') });
    ruinedModel = build(SCORCHED, 'destroyed');
  } else if (structure.type === 'antenna') {
    // Use beacon post model for antenna structures (63.7 b: sized by function, a listening post's mast in person units)
    const post = (destroyed: boolean): THREE.Group => {
      const g = buildBeaconPost(t.primary, t.accent, destroyed).group;
      if (!byFunction) return g;
      g.scale.setScalar(ANTENNA_POST_PERSON_U);
      return new THREE.Group().add(g);
    };
    intactModel = post(false);
    ruinedModel = post(true);
    makeDamaged = () => { const g = post(false); damageModel(g, 7 + structure.id); bakeStaticParts(g); return { root: g }; };
  } else {
    // 63.7 (b): sized by function, the builder's model at person scale (in person units), whoever holds it now
    const look = byFunction ? 'person' : 'classic', builder = byFunction ? structure.builder ?? teamIndex ?? 0 : teamIndex ?? 0;
    // 63.14 step 2: modular buildings on — the builder's layout drawn from the kit (rendering/moduleLook.ts), instanced in
    // the kit's model units and scaled with the group as the builder's model is (the bake leaves instanced meshes alone);
    // the damaged form a keyed share of its walls damaged; the sim reads the one box; 72.1: the ruin the layout's (every module
    // destroyed, the ground floor's walls' heaps at the sim's rubble height — moduleLook.ts buildLayoutRuin), never the builder's old broken model
    // 63.16 (7): the motor pool's compound is its own layout on its own switch (BASE_TEMPLATES.motorPool), whatever the modular one says
    const layout = byFunction ? (structure.type === 'motorPool' ? BASE_TEMPLATES.motorPool : opts.modular ? teamLayout(builder) : null) : null;
    if (layout) {
      padLayout = layout;
      kitExtent = layoutLookExtent(layout, REFERENCE.person.height);   // (in the kit's model units, as modelExtents measures: the plinth's seat scales it with the group — at the match's person it was scaled twice, a pad 1/33 of its size)
      // 72.5: a settlement's house in its settlement's masonry look (keyed by its planet and its lane), its walls, piers and roofs
      const look = structure.type === 'house' && opts.ground ? settlementLook(opts.ground, structure.yaw ?? 0) : undefined;
      intactModel = buildLayoutLook(layout, unit, REFERENCE.person.height, 'whole', 7 + structure.id, undefined, look).group;
      ruinedModel = structure.type === 'motorPool' ? buildLayoutLook(layout, unit, REFERENCE.person.height, 'damaged', 7 + structure.id).group : buildLayoutRuin(layout, unit, REFERENCE.person.height, look).group;   // (63.16 (7): the pool's ruin is its compound damaged — no team building stands for it)
      makeDamaged = () => ({ root: buildLayoutLook(layout, unit, REFERENCE.person.height, 'damaged', 7 + structure.id, undefined, look).group });
      // 63.14 step 4: the modules' own forms (a destroyed one's rubble, a worn one damaged), rebuilt when the structure's modules or rubble change
      makeModules = (s: Pick<StructureState, 'modules' | 'rubble'> & Partial<Pick<StructureState, 'shut'>>) => buildLayoutLook(layout, unit, REFERENCE.person.height, 'whole', 7 + structure.id, s, look).group;
      makeFalls = falls => buildFallingLook(layout, unit, REFERENCE.person.height, falls, 7 + structure.id, look);   // 72.3 step 3: a collapse's falls
    } else {
      intactModel = buildTeamStructure(builder, false, look);
      ruinedModel = buildTeamStructure(builder, true, look);
      makeDamaged = () => { const g = buildTeamStructure(builder, false, look); damageModel(g, 7 + structure.id); bakeStaticParts(g); return { root: g }; };
    }
  }
  // Both variants are static (we only toggle .visible between them); emissive
  // windows land in their own merged mesh, transparent parts stay separate.
  if (!personScale) {
    bakeStaticParts(intactModel);
    bakeStaticParts(ruinedModel);
  }

  const group = new THREE.Group();
  group.add(intactModel);
  group.add(ruinedModel);

  // Show correct state based on current health
  const isDestroyed = structure.health <= 0;
  intactModel.visible = !isDestroyed;
  ruinedModel.visible = isDestroyed;

  // Planet-scale buildings grow with the world they stand on; person-scale ones are already sized; sized by function, a
  // model's person (1.8) is the match's person
  const scale = personScale ? 1 : byFunction ? personHeight / REFERENCE.person.height : surfacePropScale(planetRadius);
  if (!personScale) group.scale.setScalar(scale);

  // On a pad: upright, its base level with the highest ground under it, the plinth reaching below the lowest
  let plinth: PlinthSeat | undefined;
  if (opts.ground && propSeatingOn()) {
    const ext = kitExtent ?? modelExtents(intactModel);
    // (209's follow-up: a walkable top's plinth is its deck, no wider — its skirt, rendering/standSkirts.ts, falls from the deck's edge;
    // lane F's footing follow-up: a building's plinth is its stand's square — plinthHalfPeople × PAD_OVERHANG, the sim's number
    // (core/stands.ts; the table the models are held to) — drawn as its deck the same way, so the stand walked on and the pad drawn are one)
    const top = structure.baseLift !== undefined && personScale ? STAND_TOPS[structure.type] : undefined;
    const padHalf = plinthHalfPeople(structure, byFunction, padLayout);
    const deck = top ? Math.min(top.halfXPeople, top.halfZPeople) : padHalf !== null ? padHalf * PAD_OVERHANG : null;
    plinth = seatOnPlinth(group, opts.ground, { planetIndex: opts.planets ? opts.planets.indexOf(opts.ground) : -1, dir: structure.surfaceDir, ...(structure.yaw ? { yaw: structure.yaw } : {}), half: deck !== null ? deck * personHeight : ext.half * scale, ...(deck !== null ? { overhang: 1 } : {}), height: ext.height * scale, scale, color: opts.rockColor ?? '#7a7468', fine: opts.fine, ...(structure.baseLift !== undefined ? { lift: structure.baseLift } : {}) });   // (67.52)
  } else {
    group.position.set(worldPos.x, worldPos.y, worldPos.z);
    seatQuaternion(group.quaternion, surfaceNormal, structure.yaw);   // (fix/63.14-house-yaw: turned by the structure's yaw, as the sim's frame is)
  }

  // 69.34: a dump's ammunition beside it (on the group, not the model: the plinth is measured from the model alone) — a base
  // crate's along its right, a FOB's behind its open rear, the rows going away from it (person-scale metres: × personHeight / 1.8)
  let ammo: AmmoDump | undefined;
  if ((structure.type === 'supply' || structure.type === 'fob') && structure.supply?.ammo) {
    ammo = buildAmmoDump(unit, AMMO_DUMP_PER_ROW);
    const k = personHeight / 1.8, half = (AMMO_DUMP_PER_ROW * (PALLET.width + 0.2) - 0.2) / 2;
    ammo.group.scale.setScalar(k);
    if (structure.type === 'supply') ammo.group.position.set(0.95 * k, 0, -half * k);
    else { ammo.group.position.set(-half * k, 0, -2.2 * k); ammo.group.rotation.y = Math.PI / 2; }
    showAmmoDump(ammo, structure.supply.ammo);
    ammo.group.visible = !isDestroyed;
    ammo.group.userData.dump = ammo;   // (69.31: the dump's arm stands in its frame and picks from its stacks — game/rearmArms.ts)
    group.add(ammo.group);
  }

  scene.add(group);
  const sm: StructureMesh = { group, intactModel, ruinedModel, spin, plinth, ...(ammo ? { ammo } : {}), ...(makeDamaged ? { makeDamaged } : {}), ...(makePartLost ? { makePartLost } : {}), ...(makeModules ? { makeModules } : {}), ...(makeFalls ? { makeFalls } : {}), ...(lights.length ? { lights } : {}), ...(structure.type === 'pointDefence' ? { aims } : {}), ...(structure.type === 'ecotourism' && byFunction ? { smoke } : {}), ...(load ? { load } : {}) };
  if (!isDestroyed) showStructureState(sm, structure);
  return sm;
}

/**
 * BUILDLIST 28: a structure just built grows out of the ground over the builder's hammer swing instead of popping
 * in whole — scaled from its base (its model's origin) from nothing to a touch past full and back, the pad already
 * down. Presentation only; the state has the structure standing from the build on.
 */
export function startStructureRise(m: StructureMesh, seconds = BUILD_SWING_DURATION): void {
  m.rise = { t: 0, dur: seconds, base: m.intactModel.scale.clone() };
  m.intactModel.scale.copy(m.rise.base).multiplyScalar(RISE_FLOOR);
}

/** Never quite nothing: a zero scale leaves a singular matrix behind the normals. */
const RISE_FLOOR = 1e-3;

/** The rise's scale at `p` (0 → 1 of its time): an ease-out with a small overshoot, 0 at the start, exactly 1 at the end. */
export function riseScale(p: number): number {
  const q = Math.min(1, Math.max(0, p)) - 1, k = 1.4;   // easeOutBack
  return 1 + (k + 1) * q * q * q + k * q * q;
}

/** Advance every rising structure (each frame, beside sweepStructureHeads); a finished one is left at its own scale. */
export function riseStructures(meshes: Iterable<StructureMesh>, dt: number): void {
  for (const m of meshes) {
    const r = m.rise;
    if (!r) continue;
    r.t += dt;
    if (r.t >= r.dur) { m.intactModel.scale.copy(r.base); m.rise = undefined; continue; }
    m.intactModel.scale.copy(r.base).multiplyScalar(Math.max(RISE_FLOOR, riseScale(r.t / r.dur)));
  }
}

/**
 * 69.5e: step each HQ's smoke while the form it rises from shows (the damaged form's breaches, the ruin) — each capped at
 * HQ_FALL.most puffs, one draw. Cost: ~1 µs a smoking HQ a frame.
 */
export function stepStructureSmoke(meshes: Iterable<StructureMesh>, dt: number): void {
  for (const m of meshes) {
    if (!m.smoke) continue;
    for (const sm of m.smoke) { const form = sm.mesh.parent; if (form?.visible && sm.mesh.visible) sm.step(dt); }
  }
}

/** Turn every standing structure's moving part (the tower's radar head: one sweep in 3 s). */
export function sweepStructureHeads(meshes: Iterable<StructureMesh>, dt: number): void {
  for (const m of meshes) {
    if (m.spin && m.intactModel.visible) m.spin.rotation.y += (Math.PI * 2 * dt) / 3;
    if (m.damagedSpin && m.damagedModel?.visible) m.damagedSpin.rotation.y += (Math.PI * 2 * dt) / 3;
  }
}

export function updateStructureMesh(
  structMesh: StructureMesh,
  worldPos: Vec3,
  surfaceNormal: Vec3,
  planets?: PlanetState[],
  yaw?: number,
): void {
  if (structMesh.plinth && planets) { reseatPlinth(structMesh, planets); return; }
  structMesh.group.position.set(worldPos.x, worldPos.y, worldPos.z);
  seatQuaternion(structMesh.group.quaternion, surfaceNormal, yaw);   // (fix/63.14-house-yaw: the structure's yaw, as the sim's frame)
}

export function disposeStructureMesh(scene: THREE.Scene, structMesh: StructureMesh): void {
  scene.remove(structMesh.group);
  disposeObjectTree(structMesh.group);
}

export function setStructureRuined(structMesh: StructureMesh): void {
  structMesh.intactModel.visible = false;
  if (structMesh.damagedModel) structMesh.damagedModel.visible = false;
  if (structMesh.partLostModel) structMesh.partLostModel.visible = false;
  structMesh.ruinedModel.visible = true;
}

/**
 * Keep the drawn structures in step with the state, every frame: a structure that appeared since (a beacon planted on
 * the action clock — nothing else made its mesh there) gets one through `ensure`, and one at 0 health shows its ruin
 * whatever destroyed it (a rocket's burst carries no destroyed ids, so only shells and turn shots used to swap it).
 * Cheap: a pass over the few structures. Returns how many meshes it made and ruined.
 */
export function syncStructureMeshes(structures: readonly (Pick<StructureState, 'id' | 'health' | 'type'> & Partial<Pick<StructureState, 'supply'>>)[], meshes: ReadonlyMap<number, StructureMesh>, ensure: (index: number) => void, full?: (s: Pick<StructureState, 'id' | 'type'>) => number, tick?: number): { made: number; ruined: number } {
  let made = 0, ruined = 0;
  for (let i = 0; i < structures.length; i++) {
    const s = structures[i];
    if (!meshes.has(s.id)) { ensure(i); made++; }
    const m = meshes.get(s.id);
    if (!m) continue;
    if (m.ammo) { m.ammo.group.visible = s.health > 0; if (s.health > 0) showAmmoDump(m.ammo, s.supply?.ammo); }   // 69.34: a key compare a frame; laid out again only when a crate's worth moves
    if (s.health > 0) { showStructureState(m, s, full?.(s), tick); if (m.load) showSupplyLoad(m.load, s); continue; }   // 63.4 step 8: whole or damaged, as its health says
    if (!m.ruinedModel.visible) { setStructureRuined(m); ruined++; }
  }
  return { made, ruined };
}

/**
 * 67.62: a supply dump's load from its stock — its share of what it was drawn with, in crate-layer steps (an empty pallet
 * at none); the forms' setters run only when the step changes. Cost: summing six numbers and a compare per supply dump a
 * frame; a visibility set on its three layers when the step moves.
 */
export function showSupplyLoad(load: NonNullable<StructureMesh['load']>, s: Partial<Pick<StructureState, 'supply'>>): void {
  const share = load.full > 0 ? Math.min(1, supplyStock(s) / load.full) : 1;
  const step = Math.ceil(share * SUPPLY_LOAD_STEPS - 1e-6) / SUPPLY_LOAD_STEPS;
  if (step === load.shown) return;
  load.shown = step;
  for (const set of load.sets) set(step);
}

/**
 * 69.2: the web on its lights, every frame after syncStructureMeshes (so a form made this frame takes it at once): each
 * structure with a light is off when destroyed, linked when its side's web reaches it (core/web.ts isLinked, the sim's
 * `state.webs`), else unlinked — the slow amber blink of a stranded node. Without relays there is no web: lit while it
 * stands. Rendering reads the state, never writes it. Cost: a lookup in the side's linked ids and a colour set per lit
 * node a frame (the few dozen web nodes at Foothold).
 */
export function lightWeb(state: GameState, meshes: ReadonlyMap<number, StructureMesh>, t: number): void {
  for (const s of state.structures ?? []) {
    const m = meshes.get(s.id);
    if (!m?.lights) continue;
    const link: RelayLink = s.health <= 0 ? 'off' : !state.config.relays || isLinked(state, s) ? 'linked' : 'unlinked';
    for (const f of m.lights) f(link, t);
  }
}

/**
 * 69.9: the structures whose forms move by the state (the point-defence mounts' turrets, guns and radars), every frame after
 * syncStructureMeshes; a destroyed one is still. Cost: a few trig calls and a matrix inverse a shown mount a frame.
 */
export function aimStructures(structures: readonly StructureState[], meshes: ReadonlyMap<number, StructureMesh>, dt: number): void {
  for (const s of structures) {
    if (s.type !== 'pointDefence' || s.health <= 0) continue;
    const m = meshes.get(s.id);
    if (!m?.aims) continue;
    const shown = isSite(s) && !sitePowered({ structures }, s) ? { ...s, dark: true } : s;   // (69.50 step 2b: its generator down)
    for (const f of m.aims) f(shown, dt);
  }
}

/**
 * 63.4 step 8: a standing structure drawn whole above STRUCTURE_DAMAGED_AT of its body's health, damaged at or under it
 * (its damaged form made on first need). A structure repaired back over the line shows whole again. Presentation only.
 * `full`: its full health (69.61 step 6: a story HQ's GameConfig.hqHealth, core/body.ts structureFullHealth); absent, its body's.
 */
export function showStructureState(m: StructureMesh, s: Pick<StructureState, 'health' | 'type'> & Partial<Pick<StructureState, 'parts' | 'modules' | 'rubble' | 'shut' | 'collapse'>>, full = BODIES[s.type]?.health ?? s.health, tick?: number): void {
  // 72.3 step 3: a collapse's falling modules — made again when its falls change, posed for the sim's tick each frame
  if (m.makeFalls && s.collapse?.falls !== m.fallsSeen) {
    if (m.falls) { m.group.remove(m.falls.group); disposeObjectTree(m.falls.group); m.falls = undefined; }
    if (s.collapse?.falls.length) {
      m.falls = m.makeFalls(s.collapse.falls);
      m.falls.group.position.copy(m.intactModel.position); m.falls.group.quaternion.copy(m.intactModel.quaternion); m.falls.group.scale.copy(m.rise ? m.rise.base : m.intactModel.scale);
      m.group.add(m.falls.group);
    }
    m.fallsSeen = s.collapse?.falls;
  }
  if (m.falls && tick !== undefined) m.falls.update(tick);
  // 63.14 step 4: a modular building's modules worn or fallen — the look made again from their healths (a rebuild per change, not per frame); 63.16 (b2): and its gates' leaves from its `shut` list
  if (m.makeModules && (s.modules !== m.modulesSeen || s.rubble !== m.rubbleSeen || s.shut !== m.shutSeen)) {
    const g = m.makeModules(s);
    g.position.copy(m.intactModel.position); g.quaternion.copy(m.intactModel.quaternion); g.scale.copy(m.rise ? m.rise.base : m.intactModel.scale);
    g.visible = m.intactModel.visible;
    m.group.remove(m.intactModel); disposeObjectTree(m.intactModel);
    m.group.add(g); m.intactModel = g;
    if (m.damagedModel) { m.group.remove(m.damagedModel); disposeObjectTree(m.damagedModel); m.damagedModel = undefined; m.damagedSpin = undefined; }
    m.modulesSeen = s.modules; m.rubbleSeen = s.rubble; m.shutSeen = s.shut;
  }
  // 63.4 step 7: a detaching part at 0 (part healths exist only under partial destruction) — the form without it, over the rest
  const lost = !!m.makePartLost && !!s.parts && markedParts(s.type).some((p, k) => p.detaches && (s.parts![k] ?? 1) <= 0);
  if (lost) {
    if (!m.partLostModel) {
      const d = m.makePartLost!();
      d.root.position.copy(m.intactModel.position); d.root.quaternion.copy(m.intactModel.quaternion);
      d.root.scale.copy(m.rise ? m.rise.base : m.intactModel.scale);
      m.group.add(d.root);
      m.partLostModel = d.root;
    }
    if (m.partLostModel.visible && !m.intactModel.visible && !m.ruinedModel.visible) return;
    m.partLostModel.visible = true; m.intactModel.visible = false; m.ruinedModel.visible = false;
    if (m.damagedModel) m.damagedModel.visible = false;
    return;
  }
  if (m.partLostModel) m.partLostModel.visible = false;
  const damaged = !!m.makeDamaged && !(m.makeModules && s.modules) && s.health <= STRUCTURE_DAMAGED_AT * full;   // (step 4: with module healths the modules carry the forms)
  if (damaged && !m.damagedModel) {
    const d = m.makeDamaged!();
    d.root.position.copy(m.intactModel.position); d.root.quaternion.copy(m.intactModel.quaternion);
    d.root.scale.copy(m.rise ? m.rise.base : m.intactModel.scale);
    m.group.add(d.root);
    m.damagedModel = d.root;
    m.damagedSpin = d.spin;
  }
  const showDamaged = damaged && !!m.damagedModel;
  if (m.intactModel.visible === !showDamaged && (!m.damagedModel || m.damagedModel.visible === showDamaged) && !m.ruinedModel.visible) return;
  m.intactModel.visible = !showDamaged;
  if (m.damagedModel) m.damagedModel.visible = showDamaged;
  m.ruinedModel.visible = false;
}

// =============================================================================
// RUIN RENDERING — neutral environmental structures
// =============================================================================

export interface RuinMesh {
  group: THREE.Group;
  ruinId: number;
  claimSign?: THREE.Group;   // team signpost while claimed (see updateRuinClaimMarker)
  claimKey?: string;
  /** The pad it stands on (groundSeat.ts); absent when placed the old way. */
  plinth?: PlinthSeat;
}

/** Keep a ruin's claim signpost in step with its claim state (team, pending / active / broken). */
export function updateRuinClaimMarker(rm: RuinMesh, ruin: RuinState): void {
  const key = claimSignKey(ruin) ?? undefined;
  if (key === rm.claimKey) return;
  if (rm.claimSign) { rm.group.remove(rm.claimSign); disposeObjectTree(rm.claimSign); rm.claimSign = undefined; }
  rm.claimKey = key;
  const state = claimSignState(ruin);
  if (!state || ruin.claimTeamIndex === undefined) return;
  const sign = buildClaimSign(ruin.claimTeamIndex, state);
  bakeStaticParts(sign);
  rm.group.add(sign);
  rm.claimSign = sign;
}

export function createRuinMesh(
  ruin: RuinState,
  worldPos: THREE.Vector3,
  surfaceNormal: THREE.Vector3,
  scene: THREE.Scene,
  planetRadius: number,
  opts: PropScaleOpts = {},
): RuinMesh {
  // 63.7 (b): sized by function, the ruin's person-scale model (in person units) at the match's person, on every planet
  const byFunction = opts.buildings === 'function' && opts.personHeight !== undefined;
  const model = buildRuinModel(ruin.ruinType, byFunction ? 'person' : 'classic');
  const ext = modelExtents(model);
  bakeStaticParts(model); // ruins are static: ~10 meshes → 1-2

  const group = new THREE.Group();
  group.add(model);
  const scale = byFunction ? opts.personHeight! / REFERENCE.person.height : surfacePropScale(planetRadius);
  group.scale.setScalar(scale);

  // A ruin is a building: upright on a pad, never tilted with its hill
  let plinth: PlinthSeat | undefined;
  if (opts.ground && propSeatingOn()) {
    plinth = seatOnPlinth(group, opts.ground, { planetIndex: opts.planets ? opts.planets.indexOf(opts.ground) : -1, dir: ruin.surfaceDir, half: ext.half * scale, height: ext.height * scale, scale, color: opts.rockColor ?? '#7a7468', fine: opts.fine });
  } else {
    group.position.copy(worldPos);
    group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), surfaceNormal);
  }

  scene.add(group);
  return { group, ruinId: ruin.id, plinth };
}

export function disposeRuinMesh(scene: THREE.Scene, rm: RuinMesh): void {
  scene.remove(rm.group);
  disposeObjectTree(rm.group);
}

// =============================================================================
// AIM INDICATOR
// =============================================================================

export interface AimIndicator {
  arrow: THREE.Group;
  shaft: THREE.Mesh;
  head: THREE.Mesh;
}

export function createAimIndicator(scene: THREE.Scene): AimIndicator {
  const arrow = new THREE.Group();

  // Arrow shaft
  const shaftGeo = new THREE.CylinderGeometry(0.08, 0.08, 2.5, 6);
  shaftGeo.translate(0, 1.25, 0);
  const shaftMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.7,
  });
  const shaft = new THREE.Mesh(shaftGeo, shaftMat);
  arrow.add(shaft);

  // Arrow head
  const headGeo = new THREE.ConeGeometry(0.2, 0.5, 6);
  headGeo.translate(0, 2.75, 0);
  const headMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.85,
  });
  const head = new THREE.Mesh(headGeo, headMat);
  arrow.add(head);

  arrow.visible = false;
  scene.add(arrow);

  return { arrow, shaft, head };
}

export function updateAimIndicator(
  indicator: AimIndicator,
  worldPos: Vec3,
  launchDir: Vec3,
  power: number,
  color: string
): void {
  // Position at player
  indicator.arrow.position.set(worldPos.x, worldPos.y, worldPos.z);

  // Orient to point in launch direction
  const dir = new THREE.Vector3(launchDir.x, launchDir.y, launchDir.z);
  const up = new THREE.Vector3(0, 1, 0);
  indicator.arrow.quaternion.setFromUnitVectors(up, dir);

  // Scale by power (0.5 to 1.5 range)
  const scale = 0.5 + (power / 100) * 1.0;
  indicator.arrow.scale.set(scale, scale, scale);

  // Color based on weapon/player
  (indicator.shaft.material as THREE.MeshBasicMaterial).color.set(color);
  (indicator.head.material as THREE.MeshBasicMaterial).color.set(color);

  indicator.arrow.visible = true;
}

export function hideAimIndicator(indicator: AimIndicator): void {
  indicator.arrow.visible = false;
}

// =============================================================================
// PROJECTILE RENDERING
// =============================================================================

export function createProjectileMesh(
  scene: THREE.Scene,
  weaponKey: WeaponKey
): THREE.Group {
  const group = buildProjectileModel(weaponKey);
  group.scale.setScalar(PROJECTILE_SCALE);
  scene.add(group);
  return group;
}

// Reusable objects for orientation computation (avoid per-frame allocation)
/**
 * Point the projectile's +Z along its velocity with minimal roll. The old
 * lookAt-with-up-vector approach snapped the roll whenever the flight passed
 * near vertical (the "orientation jitter" bug); parallel transport
 * (orientAlongDirection) carries the roll smoothly instead.
 */
export function orientProjectileMesh(
  group: THREE.Group,
  velocity: Vec3
): void {
  orientAlongDirection(group, velocity.x, velocity.y, velocity.z);
}

export function disposeProjectileMesh(scene: THREE.Scene, group: THREE.Group): void {
  scene.remove(group);
  disposeObjectTree(group);
}

export function createTrailLine(
  scene: THREE.Scene,
  color: string,
  maxPoints: number
): THREE.Line {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute(
    'position',
    new THREE.BufferAttribute(new Float32Array(maxPoints * 3), 3)
  );
  geo.setDrawRange(0, 0);
  const mat = new THREE.LineBasicMaterial({
    color,
    transparent: true,
    opacity: 0.4,
  });
  const line = new THREE.Line(geo, mat);
  line.frustumCulled = false;
  scene.add(line);
  return line;
}

export function updateTrailLine(
  line: THREE.Line,
  points: Vec3[]
): void {
  const posArr = line.geometry.attributes.position.array as Float32Array;
  for (let i = 0; i < points.length; i++) {
    posArr[i * 3] = points[i].x;
    posArr[i * 3 + 1] = points[i].y;
    posArr[i * 3 + 2] = points[i].z;
  }
  line.geometry.attributes.position.needsUpdate = true;
  line.geometry.setDrawRange(0, points.length);
}

// =============================================================================
// TRAJECTORY PREVIEW
// =============================================================================

export function createPreviewLine(
  scene: THREE.Scene,
  points: Vec3[]
): THREE.Line {
  const geo = new THREE.BufferGeometry();
  const arr = new Float32Array(points.length * 3);
  for (let i = 0; i < points.length; i++) {
    arr[i * 3] = points[i].x;
    arr[i * 3 + 1] = points[i].y;
    arr[i * 3 + 2] = points[i].z;
  }
  geo.setAttribute('position', new THREE.BufferAttribute(arr, 3));

  const mat = new THREE.LineDashedMaterial({
    color: 0xc0b8a8,
    transparent: true,
    opacity: 0.25,
    dashSize: 0.6,
    gapSize: 0.3,
  });

  const line = new THREE.Line(geo, mat);
  line.frustumCulled = false;
  line.computeLineDistances();
  scene.add(line);
  return line;
}

export function removeFromScene(scene: THREE.Scene, obj: THREE.Object3D | null): void {
  if (obj) {
    scene.remove(obj);
    disposeObjectTree(obj);
  }
}

/**
 * The flat-shaded normal made safe: three builds it per pixel as normalize(cross(dFdx(p), dFdy(p))), and on a microscopic
 * triangle (an instance shrunk toward nothing near the camera — the far canopy's grow-in, a stand-in's fade) the derivatives
 * vanish and normalize(0) is NaN. One NaN pixel is spread by the bloom's blur into a hard-edged black block across half the
 * screen (lane E's black right half beside a tank, Oct 23: two NaN pixels on a far canopy blob 0.1 units from the camera).
 * With the cross product too small, the view's own axis stands in. Patched once, in the shared chunk, for every flat material.
 */
export function safeFlatNormals(chunks: Record<string, string> = THREE.ShaderChunk as unknown as Record<string, string>): void {
  const flat = 'vec3 normal = normalize( cross( fdx, fdy ) );';
  const chunk = chunks.normal_fragment_begin;
  if (!chunk || !chunk.includes(flat)) return;   // (patched already, or a three that builds it otherwise)
  chunks.normal_fragment_begin = chunk.replace(flat, 'vec3 normal = cross( fdx, fdy ); normal = dot( normal, normal ) > 0.0 ? normalize( normal ) : vec3( 0.0, 0.0, 1.0 );');
}

// =============================================================================
// WINDOW RESIZE
// =============================================================================

export function handleResize(sceneObjs: SceneObjects): void {
  const w = window.innerWidth, h = window.innerHeight;
  sceneObjs.camera.aspect = w / h;
  sceneObjs.camera.updateProjectionMatrix();
  sceneObjs.renderer.setSize(w, h);
  sceneObjs.composer?.setSize(w, h);
}

// =============================================================================
// RENDER
// =============================================================================

export function render(sceneObjs: SceneObjects): void {
  // Accumulate renderer.info across all composer passes (autoReset would
  // leave only the last fullscreen pass in the draw-call counter)
  sceneObjs.renderer.info.autoReset = false;
  sceneObjs.renderer.info.reset();
  // 62.64 (b): the draw's matrix walk leaves the hidden objects marked to be skipped (rendering/hiddenWalk.ts)
  drawSkippingHidden(() => {
    if (sceneObjs.composer) sceneObjs.composer.render();
    else sceneObjs.renderer.render(sceneObjs.scene, sceneObjs.camera);
  });
}

// =============================================================================
// MOVEMENT RING
// =============================================================================

const MOVEMENT_RING_SEGMENTS = 48;

export function createMovementRing(scene: THREE.Scene): MovementRing {
  // Ring showing movement range
  const ringGeo = new THREE.BufferGeometry();
  const ringVerts = new Float32Array((MOVEMENT_RING_SEGMENTS + 1) * 3);
  ringGeo.setAttribute('position', new THREE.BufferAttribute(ringVerts, 3));

  const ringMat = new THREE.LineBasicMaterial({
    color: 0x88c4a0,
    transparent: true,
    opacity: 0.6,
    depthTest: false,
    depthWrite: false,
  });

  const ring = new THREE.Line(ringGeo, ringMat);
  ring.visible = false;
  ring.renderOrder = 999;
  scene.add(ring);

  // Dot showing target position
  const dotGeo = new THREE.SphereGeometry(0.15, 8, 6);
  const dotMat = new THREE.MeshBasicMaterial({
    color: 0xa0c4b8,
    transparent: true,
    opacity: 0.8,
    depthTest: false,
  });
  const dot = new THREE.Mesh(dotGeo, dotMat);
  dot.visible = false;
  dot.renderOrder = 999;
  scene.add(dot);

  // Cost label sprite
  const costCanvas = document.createElement('canvas');
  costCanvas.width = 128;
  costCanvas.height = 64;
  const costTexture = new THREE.CanvasTexture(costCanvas);
  costTexture.minFilter = THREE.LinearFilter;
  const costMat = new THREE.SpriteMaterial({
    map: costTexture,
    transparent: true,
    depthTest: false,
  });
  const costLabel = new THREE.Sprite(costMat);
  costLabel.scale.set(1.0, 0.5, 1);
  costLabel.visible = false;
  scene.add(costLabel);

  return { ring, dot, costLabel, costCanvas, costTexture };
}

function getSurfaceRadiusAt(
  heightmap: Float32Array,
  basePositions: Float32Array,
  radius: number,
  dirX: number, dirY: number, dirZ: number
): number {
  return getSurfaceRadiusRaw(heightmap, basePositions, radius, dirX, dirY, dirZ);
}

export function updateMovementRing(
  movementRing: MovementRing,
  planetPos: Vec3,
  playerDir: Vec3,
  planetRadius: number,
  movementRange: number,
  heightmap: Float32Array,
  basePositions: Float32Array,
  targetDir: Vec3 | null = null
): void {
  const maxAngle = movementRange / planetRadius;

  // Build orthonormal basis centered on playerDir
  const center = new THREE.Vector3(playerDir.x, playerDir.y, playerDir.z).normalize();

  let ref = new THREE.Vector3(1, 0, 0);
  if (Math.abs(center.dot(ref)) > 0.9) {
    ref = new THREE.Vector3(0, 1, 0);
  }

  const tangent = new THREE.Vector3().crossVectors(center, ref).normalize();
  const bitangent = new THREE.Vector3().crossVectors(center, tangent).normalize();

  // Generate ring points on planet surface
  const posArr = movementRing.ring.geometry.attributes.position.array as Float32Array;

  for (let i = 0; i <= MOVEMENT_RING_SEGMENTS; i++) {
    const angle = (i / MOVEMENT_RING_SEGMENTS) * Math.PI * 2;
    const ca = Math.cos(angle);
    const sa = Math.sin(angle);
    const ce = Math.cos(maxAngle);
    const se = Math.sin(maxAngle);

    // Rotate from center by maxAngle in direction of angle
    const dirX = center.x * ce + (tangent.x * ca + bitangent.x * sa) * se;
    const dirY = center.y * ce + (tangent.y * ca + bitangent.y * sa) * se;
    const dirZ = center.z * ce + (tangent.z * ca + bitangent.z * sa) * se;

    // Position on planet surface (slightly above), following heightmap
    const surfaceR = getSurfaceRadiusAt(heightmap, basePositions, planetRadius, dirX, dirY, dirZ) + 0.15;
    posArr[i * 3] = planetPos.x + dirX * surfaceR;
    posArr[i * 3 + 1] = planetPos.y + dirY * surfaceR;
    posArr[i * 3 + 2] = planetPos.z + dirZ * surfaceR;
  }

  movementRing.ring.geometry.attributes.position.needsUpdate = true;
  movementRing.ring.visible = true;

  // Update target dot
  if (targetDir) {
    const td = new THREE.Vector3(targetDir.x, targetDir.y, targetDir.z).normalize();
    const dotR = getSurfaceRadiusAt(heightmap, basePositions, planetRadius, td.x, td.y, td.z) + 0.2;
    movementRing.dot.position.set(
      planetPos.x + td.x * dotR,
      planetPos.y + td.y * dotR,
      planetPos.z + td.z * dotR
    );
    movementRing.dot.visible = true;
  } else {
    movementRing.dot.visible = false;
  }
}

export function updateMovementCostLabel(
  movementRing: MovementRing,
  cost: number,
  canAfford: boolean
): void {
  const ctx = movementRing.costCanvas.getContext('2d')!;
  ctx.clearRect(0, 0, 128, 64);
  ctx.font = 'bold 36px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = canAfford ? '#88c4a0' : '#ff6666';
  ctx.fillText(cost.toFixed(1), 64, 32);
  movementRing.costTexture.needsUpdate = true;

  // Position above the dot
  const dotPos = movementRing.dot.position;
  if (movementRing.dot.visible) {
    const len = Math.sqrt(dotPos.x ** 2 + dotPos.y ** 2 + dotPos.z ** 2);
    if (len > 0) {
      const nx = dotPos.x / len;
      const ny = dotPos.y / len;
      const nz = dotPos.z / len;
      movementRing.costLabel.position.set(
        dotPos.x + nx * 0.6,
        dotPos.y + ny * 0.6,
        dotPos.z + nz * 0.6
      );
    }
    movementRing.costLabel.visible = true;
  } else {
    movementRing.costLabel.visible = false;
  }
}

export function hideMovementRing(movementRing: MovementRing): void {
  movementRing.ring.visible = false;
  movementRing.dot.visible = false;
  movementRing.costLabel.visible = false;
}

export function disposeMovementRing(scene: THREE.Scene, movementRing: MovementRing): void {
  scene.remove(movementRing.ring);
  scene.remove(movementRing.dot);
  scene.remove(movementRing.costLabel);
  movementRing.ring.geometry.dispose();
  (movementRing.ring.material as THREE.Material).dispose();
  movementRing.dot.geometry.dispose();
  (movementRing.dot.material as THREE.Material).dispose();
  (movementRing.costLabel.material as THREE.Material).dispose();
  movementRing.costTexture.dispose();
}

// =============================================================================
// BOT THINKING INDICATOR
// =============================================================================

export interface BotThinkingSprite {
  sprite: THREE.Sprite;
  texture: THREE.CanvasTexture;
}

export function createBotThinkingSprite(scene: THREE.Scene): BotThinkingSprite {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.font = 'bold 48px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#ffcc44';
  ctx.fillText('?', 32, 32);

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(0.8, 0.8, 1);
  sprite.visible = false;
  scene.add(sprite);

  return { sprite, texture };
}

export function showBotThinking(indicator: BotThinkingSprite, worldPos: Vec3, surfaceDir: Vec3): void {
  // Position above the bot's head
  indicator.sprite.position.set(
    worldPos.x + surfaceDir.x * 1.2,
    worldPos.y + surfaceDir.y * 1.2,
    worldPos.z + surfaceDir.z * 1.2
  );
  indicator.sprite.visible = true;
}

export function hideBotThinking(indicator: BotThinkingSprite): void {
  indicator.sprite.visible = false;
}

export function disposeBotThinking(scene: THREE.Scene, indicator: BotThinkingSprite): void {
  scene.remove(indicator.sprite);
  (indicator.sprite.material as THREE.Material).dispose();
  indicator.texture.dispose();
}

// =============================================================================
// HAZARD MESHES
// =============================================================================

export interface HazardMesh {
  group: THREE.Group;
  /** How it sits on the ground (groundSeat.ts); absent when placed the old way. */
  seat?: PropSeat;
  hazardType: HazardType;
  hazardId: number;
  pool: THREE.Mesh | null;                      // lava / mineral pool (opacity pulse)
  crystalMats: THREE.MeshLambertMaterial[];     // the baked crystal buckets (three shimmer phases)
  wisps: THREE.InstancedMesh | null;            // smoke / steam puffs, one draw
  wispRadius: number;
  ventY: number;                                // local height wisps respawn at
  animData: {
    baseTime: number;
    particlePos: Array<{ x: number; y: number; z: number }>;
    particleVelocities: Array<{ x: number; y: number; z: number }>;
    particleLifetimes: number[];
    particleMaxLifetimes: number[];
    erupting: boolean;
    eruptionTimer: number;
  };
}

export function createHazardMesh(
  hazard: HazardState,
  worldPos: THREE.Vector3,
  surfaceNormal: THREE.Vector3,
  scene: THREE.Scene,
  planetRadius: number,
  opts: PropScaleOpts = {},
): HazardMesh {
  const model = buildHazardModel(hazard.hazardType, hazard.id);
  const ext = modelExtents(model.group);
  // Everything merges except the pool (its own opacity pulse); the wisps are an
  // InstancedMesh (noBake) and the crystals merge into their three shared buckets.
  bakeStaticParts(model.group, { exclude: [model.pool] });
  const crystalMats: THREE.MeshLambertMaterial[] = [];
  model.group.traverse(o => {
    const m = (o as THREE.Mesh).material as THREE.MeshLambertMaterial | undefined;
    if ((o as THREE.Mesh).isMesh && o !== model.pool && m && m.isMeshLambertMaterial && m.transparent && m.emissive.getHex() !== 0 && !crystalMats.includes(m)) crystalMats.push(m);
  });
  const group = model.group;
  const scale = hazard.scale * 3 * (surfacePropScale(planetRadius) / 2);
  group.scale.setScalar(scale);
  // A vent or a cone lies on the ground's frame; a crystal cluster sits on it like a rock (both sink a little)
  const seat = placeProp(group, worldPos, surfaceNormal, opts.ground, { dir: hazard.surfaceDir, kind: hazard.hazardType === 'crystal' ? 'rock' : 'flat', half: ext.half * scale, height: ext.height * scale }, opts.planets, opts.fine);
  // Wisp motion is visual noise, never game state: plain Math.random is fine here.
  const particlePos: Array<{ x: number; y: number; z: number }> = [];
  const particleVelocities: Array<{ x: number; y: number; z: number }> = [];
  const particleLifetimes: number[] = [];
  const particleMaxLifetimes: number[] = [];
  const volcanic = hazard.hazardType === 'volcanic';
  for (let i = 0; i < (model.wisps ? model.wisps.count : 0); i++) {
    particlePos.push({ x: 0, y: model.ventY, z: 0 });
    particleVelocities.push(volcanic
      ? { x: (Math.random() - 0.5) * 0.3, y: 0.5 + Math.random() * 0.3, z: (Math.random() - 0.5) * 0.3 }
      : { x: (Math.random() - 0.5) * 0.15, y: 0.3 + Math.random() * 0.2, z: (Math.random() - 0.5) * 0.15 });
    particleLifetimes.push(Math.random() * (volcanic ? 2 : 2.5));
    particleMaxLifetimes.push(volcanic ? 1.5 + Math.random() * 1.0 : 1.2 + Math.random() * 0.8);
  }
  scene.add(group);
  return {
    group,
    seat,
    hazardType: hazard.hazardType,
    hazardId: hazard.id,
    pool: model.pool,
    crystalMats,
    wisps: model.wisps,
    wispRadius: model.wispRadius,
    ventY: model.ventY,
    animData: {
      baseTime: Math.random() * 10,
      particlePos,
      particleVelocities,
      particleLifetimes,
      particleMaxLifetimes,
      erupting: false,
      eruptionTimer: 0,
    },
  };
}

const _wispPos = new THREE.Vector3(), _wispScale = new THREE.Vector3(), _wispQuat = new THREE.Quaternion(), _wispMat = new THREE.Matrix4();
let _wispDrift: THREE.Vector3 | null = null;
/** Advance one hazard's puffs: rise, drift, grow, shrink out; respawn at the vent. One matrix write per puff, one draw per hazard. */
function updateWisps(hm: HazardMesh, dt: number, spread: number, riseLo: number, riseHi: number, jitter: number, eruptMult: number): void {
  const wisps = hm.wisps;
  if (!wisps) return;
  const ad = hm.animData;
  for (let i = 0; i < wisps.count; i++) {
    const p = ad.particlePos[i], vel = ad.particleVelocities[i];
    ad.particleLifetimes[i] += dt;
    const life = ad.particleLifetimes[i];
    const maxLife = ad.particleMaxLifetimes[i] / eruptMult;
    if (life >= maxLife) {
      p.x = (Math.random() - 0.5) * jitter; p.y = hm.ventY; p.z = (Math.random() - 0.5) * jitter;
      ad.particleLifetimes[i] = 0;
      ad.particleVelocities[i] = { x: (Math.random() - 0.5) * spread * eruptMult, y: (riseLo + Math.random() * (riseHi - riseLo)) * eruptMult, z: (Math.random() - 0.5) * spread * eruptMult };
    } else {
      p.x += vel.x * dt; p.y += vel.y * dt; p.z += vel.z * dt;
      if (_wispDrift) { const f = Math.min(1, life / maxLife); p.x += _wispDrift.x * f * dt; p.y += _wispDrift.y * f * dt; p.z += _wispDrift.z * f * dt; }
    }
    const frac = Math.min(1, ad.particleLifetimes[i] / maxLife);
    const alpha = frac < 0.2 ? frac / 0.2 : 1 - (frac - 0.2) / 0.8;       // fade in, then out — expressed as size
    const s = hm.wispRadius * (0.6 + 1.2 * frac) * Math.sqrt(Math.max(0, alpha));
    _wispPos.set(p.x, p.y, p.z); _wispScale.setScalar(Math.max(s, 1e-4));
    wisps.setMatrixAt(i, _wispMat.compose(_wispPos, _wispQuat, _wispScale));
  }
  wisps.instanceMatrix.needsUpdate = true;
}

export function updateHazardMeshes(hazardMeshes: HazardMesh[], dt: number, time: number, drift?: (hm: HazardMesh) => THREE.Vector3 | null): void {
  for (const hm of hazardMeshes) {
    _wispDrift = drift?.(hm) ?? null;   // 67.36: the wind's drift in the hazard's own frame (the puffs lean downwind as they rise)
    const t = time + hm.animData.baseTime;
    // Tick eruption timer
    if (hm.animData.erupting) {
      hm.animData.eruptionTimer -= dt;
      if (hm.animData.eruptionTimer <= 0) {
        hm.animData.erupting = false;
        hm.animData.eruptionTimer = 0;
      }
    }
    if (hm.hazardType === 'volcanic') {
      const isErupting = hm.animData.erupting;
      if (hm.pool) {
        (hm.pool.material as THREE.MeshLambertMaterial).opacity = isErupting ? 0.85 : 0.5 + 0.15 * Math.sin(t * 2.0);
      }
      if (hm.wisps) (hm.wisps.material as THREE.MeshBasicMaterial).opacity = isErupting ? 0.6 : 0.35;
      updateWisps(hm, dt, 0.3, 0.5, 0.8, 0.1, isErupting ? 2.0 : 1.0);
    } else if (hm.hazardType === 'geyser') {
      updateWisps(hm, dt, 0.15, 0.3, 0.5, 0.08, 1.0);
    } else {
      // Crystal: three shimmer phases across the formation's three material buckets
      for (let i = 0; i < hm.crystalMats.length; i++) hm.crystalMats[i].opacity = 0.72 + 0.08 * Math.sin(t * 1.8 + i * 2.1);
    }
  }
}

export function disposeHazardMesh(scene: THREE.Scene, hm: HazardMesh): void {
  scene.remove(hm.group);
  disposeObjectTree(hm.group);
}

// =============================================================================
// PLANTED FLAG MESHES
// =============================================================================

export function createPlantedFlagMesh(
  flag: PlantedFlagState,
  worldPos: Vec3,
  surfaceNormal: Vec3,
  scene: THREE.Scene,
  planetRadius: number
): PlantedFlagMesh {
  const normal = new THREE.Vector3(surfaceNormal.x, surfaceNormal.y, surfaceNormal.z);
  const team = TEAMS[flag.teamIndex % TEAMS.length];

  // Sovereignty stakes (have HP) use beacon post model
  if (flag.health !== undefined) {
    const beaconModel = buildBeaconPost(team.primary, team.accent, flag.alive === false);
    const group = beaconModel.group;
    bakeStaticParts(group, { exclude: [beaconModel.beacon] }); // beacon light keeps its own material (blink)

    group.position.set(
      worldPos.x + normal.x * 0.05,
      worldPos.y + normal.y * 0.05,
      worldPos.z + normal.z * 0.05
    );
    const up = new THREE.Vector3(0, 1, 0);
    group.quaternion.setFromUnitVectors(up, normal);
    group.scale.setScalar(surfacePropScale(planetRadius));

    scene.add(group);
    return { group, beacon: beaconModel.beacon ?? undefined, ptLight: beaconModel.ptLight ?? undefined };
  }

  // Dominion flags use pole+fabric model
  const group = new THREE.Group();

  group.position.set(
    worldPos.x + normal.x * 0.05,
    worldPos.y + normal.y * 0.05,
    worldPos.z + normal.z * 0.05
  );

  const up = new THREE.Vector3(0, 1, 0);
  group.quaternion.setFromUnitVectors(up, normal);

  // Pole
  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.02, 0.02, 1.0, 6),
    new THREE.MeshLambertMaterial({ color: '#c8cad0' })
  );
  pole.position.y = 0.5;
  group.add(pole);

  // Cap sphere
  const cap = new THREE.Mesh(
    new THREE.SphereGeometry(0.03, 6, 6),
    new THREE.MeshLambertMaterial({ color: '#c8cad0' })
  );
  cap.position.y = 1.0;
  group.add(cap);

  // Fabric with team texture
  const fabricMat = new THREE.MeshLambertMaterial({
    side: THREE.DoubleSide,
    color: new THREE.Color(team.accent),
    map: getFlagTexture(flag.teamIndex),
  });
  const fabric = new THREE.Mesh(
    new THREE.PlaneGeometry(0.5, 0.35),
    fabricMat
  );
  fabric.position.set(0.27, 0.82, 0);
  group.add(fabric);

  scene.add(group);

  return { group, fabric };
}

export function disposePlantedFlagMesh(scene: THREE.Scene, pfm: PlantedFlagMesh): void {
  scene.remove(pfm.group);
  disposeObjectTree(pfm.group);
}

export function updatePlantedFlags(flagMeshes: Map<number, PlantedFlagMesh>, time: number): void {
  for (const [, pfm] of flagMeshes) {
    // Dominion: fabric wave
    if (pfm.fabric) {
      pfm.fabric.rotation.y = Math.sin(time * 2.5) * 0.12;
    }
    // Sovereignty: beacon blink
    if (pfm.beacon) {
      const cycle = time % 3.5;
      let intensity = 0;
      if (cycle < 0.15) {
        intensity = 1.0;
      } else if (cycle >= 1.35 && cycle < 1.5) {
        intensity = 1.0;
      }
      // Smooth edges
      if (cycle < 0.05) intensity = cycle / 0.05;
      else if (cycle > 0.1 && cycle < 0.15) intensity = (0.15 - cycle) / 0.05;
      else if (cycle >= 1.35 && cycle < 1.4) intensity = (cycle - 1.35) / 0.05;
      else if (cycle >= 1.45 && cycle < 1.5) intensity = (1.5 - cycle) / 0.05;

      (pfm.beacon.material as THREE.MeshLambertMaterial).emissiveIntensity = 0.1 + intensity * 0.9;
      if (pfm.ptLight) pfm.ptLight.intensity = intensity * 0.6;
    }
  }
}

// =============================================================================
// FLORA MESHES — ambient vegetation / fauna
// =============================================================================

export interface FloraMesh {
  group: THREE.Group;
  model: THREE.Group;                    // the plant itself (inside the surface-aligned group); sways as a unit
  floraId: number;
  floraType: FloraType;
  animData: FloraAnimData;
  sway: { phase: number; speed: number; amp: number };
  pulseMats: THREE.MeshLambertMaterial[]; // merged emissive buckets (lichen patches, lantern glows) — gentle pulse
  /** How it sits on the ground (groundSeat.ts); absent when placed the old way (radial, base on the centre point). */
  seat?: PropSeat;
  /** A tree's ground tufts, turned to lie on the ground's plane rather than the trunk's. */
  tufts?: THREE.Group;
}

const TREE_TYPES: Set<string> = new Set(['spirePine', 'canopyOak', 'frondPalm']);

/**
 * What a normal-size (person-scaled) biome prop needs from the match: a person's height, and the planet's rock
 * colour. `ground` is the planet the prop stands on: with it, the prop is seated on the ground's own frame
 * (groundSeat.ts) instead of stood along the radial.
 */
export interface PropScaleOpts { personHeight?: number; rockColor?: string; ground?: PlanetState; planets?: PlanetState[]; fine?: FineGround; /** 63.7 (b): how the planet-scale buildings and ruins are sized (core/buildingScale.ts) */ buildings?: 'function' | 'planet'; /** 63.14 step 2: the team buildings drawn from the kit by their layouts (core/modules.ts modularBuildingsOn) */ modular?: boolean }

/** Seat a prop on its planet, or fall back to the old placement when there is no planet or seating is off. */
function placeProp(group: THREE.Group, worldPos: THREE.Vector3, surfaceNormal: THREE.Vector3, ground: PlanetState | undefined, seat: Omit<PropSeat, 'planetIndex'>, planets?: PlanetState[], fine?: FineGround): PropSeat | undefined {
  if (ground && propSeatingOn()) {
    const planetIndex = planets ? planets.indexOf(ground) : -1;
    const full: PropSeat = { ...seat, planetIndex, fine };
    seatGroup(group, ground, full);
    return full;
  }
  group.position.copy(worldPos);
  group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), surfaceNormal);
  return undefined;
}

/** Seat a flora or rock mesh again after the ground under it changed (a crater). No-op for meshes placed the old way. */
export function reseatPropMesh(pm: { group: THREE.Group; seat?: PropSeat; tufts?: THREE.Group }, planets: PlanetState[]): void {
  if (!pm.seat) return;
  const planet = planets[pm.seat.planetIndex];
  if (!planet) return;
  seatGroup(pm.group, planet, pm.seat);
  if (pm.tufts) pm.tufts.quaternion.copy(groundRotationWithin(pm.group, planet, pm.seat.dir));
}
/**
 * Normal-size biome trees and rocks are a person's scenery: beyond this many person-heights from the
 * camera they are not drawn at all (a THREE.LOD with an empty far level — the renderer switches it), so
 * the commander's and the orbit's views never pay for them (E5 / decision 87).
 */
export const NORMAL_PROP_DRAW_PERSONS = 800;   // was 400: trees popped in and out within sight at person scale (owner, Oct 10)

function farEmpty(model: THREE.Object3D, distance: number): THREE.LOD {
  const lod = new THREE.LOD();
  lod.addLevel(model, 0);
  lod.addLevel(new THREE.Object3D(), distance);
  return lod;
}

/** A normal-size biome tree (decision 87): the biome's colours, its ground cover at the foot, sized from the person. */
function createNormalFloraMesh(flora: FloraState, worldPos: THREE.Vector3, surfaceNormal: THREE.Vector3, scene: THREE.Scene, personHeight: number, ground?: PlanetState, planets?: PlanetState[], fine?: FineGround): FloraMesh {
  const biome = flora.biome ?? 'broadleaf';
  const kit = (TREE_SPECIES as readonly string[]).includes(flora.floraType);
  const model = kit
    ? buildTree(flora.floraType as TreeSpecies, BIOME_COLORS[biome], flora.scale, flora.id + 1).group
    : buildFloraModel(flora.floraType, flora.scale, flora.id + 1).group;
  const rng = new SeededRandom(`flora-ground-${flora.id}`);
  const kinds = BIOME_GROUND[biome];
  // The tufts sit in their own group so they can lie on the ground's plane while the trunk stays upright
  const tufts = new THREE.Group();
  for (let i = 0, n = rng.int(1, 2); i < n; i++) {
    const tuft = buildFloraModel(kinds[i % kinds.length], 1, flora.id * 7 + i + 1).group;
    const a = rng.range(0, Math.PI * 2), r = rng.range(0.05, 0.14);
    tuft.scale.multiplyScalar(GROUND_TUFT_SCALE[kinds[i % kinds.length]] ?? 0.2); tuft.position.set(Math.cos(a) * r, 0, Math.sin(a) * r);
    tufts.add(tuft);
  }
  const extents = modelExtents(model);
  bakeStaticParts(model);
  bakeStaticParts(tufts);
  const pulseMats: THREE.MeshLambertMaterial[] = [];
  model.traverse(o => {
    const m = (o as THREE.Mesh).material as THREE.MeshLambertMaterial | undefined;
    if ((o as THREE.Mesh).isMesh && m && m.isMeshLambertMaterial && m.emissive.getHex() !== 0 && !pulseMats.includes(m)) { m.userData.baseEmissive = m.emissiveIntensity; pulseMats.push(m); }
  });
  const swayRng = new SeededRandom(`flora-sway-${flora.id}`);
  const sway = { phase: swayRng.range(0, Math.PI * 2), speed: 0.6 + swayRng.range(0, 0.8), amp: 0.012 };
  const group = new THREE.Group();
  const near = new THREE.Group();
  near.add(model, tufts);
  group.add(farEmpty(near, NORMAL_PROP_DRAW_PERSONS * personHeight));
  const scale = NORMAL_TREE_PERSONS * personHeight;
  group.scale.setScalar(scale);
  const trunk = (TREE_TRUNK_SIZE[flora.floraType]?.half ?? extents.half * 0.1) * scale;
  const seat = placeProp(group, worldPos, surfaceNormal, ground, { dir: flora.surfaceDir, kind: kit ? 'tree' : 'flat', half: extents.half * scale, height: extents.height * scale, trunk, ...(kit ? { seatHalf: 0 } : {}) }, planets, fine);
  if (seat && ground) tufts.quaternion.copy(groundRotationWithin(group, ground, flora.surfaceDir));
  scene.add(group);
  return { group, model, floraId: flora.id, floraType: flora.floraType, animData: emptyAnimData(), sway, pulseMats, seat, tufts: seat ? tufts : undefined };
}

export function createFloraMesh(
  flora: FloraState,
  worldPos: THREE.Vector3,
  surfaceNormal: THREE.Vector3,
  scene: THREE.Scene,
  planetRadius: number,
  opts: PropScaleOpts = {},
): FloraMesh {
  if (flora.size === 'normal') return createNormalFloraMesh(flora, worldPos, surfaceNormal, scene, opts.personHeight ?? 0.055, opts.ground, opts.planets, opts.fine);
  const { group: model, animData } = buildFloraModel(flora.floraType, flora.scale, flora.id + 1);   // seeded by id: peers grow the same plant
  const extents = modelExtents(model);
  // Merge everything static — including the parts that used to sway on their own.
  // A whole plant swaying as one unit reads the same at game scale and costs one
  // or two draws instead of a dozen; only skater proxies and spore points stay.
  bakeStaticParts(model, { exclude: animData.skaterStates?.map(s => s.group) ?? [] });
  animData.swayTargets = [];
  const pulseMats: THREE.MeshLambertMaterial[] = [];
  model.traverse(o => {
    const m = (o as THREE.Mesh).material as THREE.MeshLambertMaterial | undefined;
    if ((o as THREE.Mesh).isMesh && m && m.isMeshLambertMaterial && m.emissive.getHex() !== 0 && !pulseMats.includes(m)) { m.userData.baseEmissive = m.emissiveIntensity; pulseMats.push(m); }
  });
  const swayRng = new SeededRandom(`flora-sway-${flora.id}`);
  const sway = { phase: swayRng.range(0, Math.PI * 2), speed: 0.6 + swayRng.range(0, 0.8), amp: TREE_TYPES.has(flora.floraType) ? 0.015 : 0.03 };
  const group = new THREE.Group();
  group.add(model);

  // Trees are larger than other flora; everything grows with the planet
  const tree = TREE_TYPES.has(flora.floraType);
  const scale = (tree ? TREE_DRAW_SCALE : FLORA_DRAW_SCALE) * (surfacePropScale(planetRadius) / 2);
  group.scale.setScalar(scale);

  // A tree grows along the radial and sinks what its trunk needs on the tilt; anything else lies on the ground's frame
  const trunk = (TREE_TRUNK_SIZE[flora.floraType]?.half ?? extents.half * 0.1) * scale;
  const seat = placeProp(group, worldPos, surfaceNormal, opts.ground, { dir: flora.surfaceDir, kind: tree ? 'tree' : 'flat', half: extents.half * scale, height: extents.height * scale, trunk, ...(tree ? { seatHalf: 0 } : {}) }, opts.planets, opts.fine);

  scene.add(group);

  return { group, model, floraId: flora.id, floraType: flora.floraType, animData, sway, pulseMats, seat };
}

export function updateFloraMeshes(floraMeshes: FloraMesh[], dt: number, time: number, windLevel = 0): void {
  // 62.47: the stands sway harder as the wind rises at the viewer (0 the still air's sway, 1 a storm's, twice it)
  const windSway = 0.6 + 1.4 * Math.min(1, Math.max(0, windLevel));
  for (const fm of floraMeshes) {
    const ad = fm.animData;
    // --- Whole-plant sway (poolskater pools stay still) ---
    if (fm.floraType !== 'poolskater') {
      const { phase, speed, amp } = fm.sway;
      fm.model.rotation.x = Math.sin(time * speed + phase) * amp * windSway;
      fm.model.rotation.z = Math.cos(time * speed * 0.7 + phase + 1.5) * amp * 0.75 * windSway;
    }
    // --- Emissive pulse on the merged glow buckets (lichen shimmer, lantern glow) ---
    for (let i = 0; i < fm.pulseMats.length; i++) {
      const m = fm.pulseMats[i];
      m.emissiveIntensity = (m.userData.baseEmissive as number) * (0.75 + 0.25 * Math.sin(time * fm.sway.speed * 0.8 + fm.sway.phase + i * 1.3));
    }
    // Spore drift
    if (ad.sporePoints && ad.sporeDrifts) {
      const posAttr = ad.sporePoints.geometry.attributes.position;
      const arr = posAttr.array as Float32Array;
      for (let i = 0; i < ad.sporeDrifts.length; i++) {
        const d = ad.sporeDrifts[i];
        arr[i * 3] += d.dx * dt;
        arr[i * 3 + 1] += d.dy * dt;
        arr[i * 3 + 2] += d.dz * dt;
        // Reset when too high
        if (arr[i * 3 + 1] > 0.2) {
          arr[i * 3] = (Math.random() - 0.5) * 0.12;
          arr[i * 3 + 1] = 0;
          arr[i * 3 + 2] = (Math.random() - 0.5) * 0.12;
        }
      }
      posAttr.needsUpdate = true;
    }

    // --- Poolskater behavior ---
    if (ad.skaterStates && ad.poolRadius !== undefined) {
      const poolR = ad.poolRadius * 0.85; // keep inside rim
      for (const s of ad.skaterStates) {
        s.timer += dt;

        if (s.state === 'paused') {
          s.speed *= 0.92;
          if (s.timer > s.duration) {
            s.state = 'darting';
            s.timer = 0;
            s.heading = Math.random() * Math.PI * 2;
            s.speed = 0.5 + Math.random() * 1.5;
            s.duration = 0.2 + Math.random() * 0.3;
          }
        } else {
          if (s.timer > s.duration) {
            s.state = 'paused';
            s.timer = 0;
            s.duration = 0.3 + Math.random() * 2.0;
          }
        }

        // Move
        s.x += Math.cos(s.heading) * s.speed * dt;
        s.z += Math.sin(s.heading) * s.speed * dt;

        // Keep in pool
        const dist = Math.sqrt(s.x * s.x + s.z * s.z);
        if (dist > poolR) {
          s.heading = Math.atan2(-s.z, -s.x) + (Math.random() - 0.5) * 1.0;
          const norm = poolR / dist;
          s.x *= norm;
          s.z *= norm;
        }

        s.group.position.x = s.x;
        s.group.position.z = s.z;
        s.group.rotation.y = s.heading;
        s.group.position.y = 0.01 + Math.sin(time * 8 + s.heading) * 0.001;
      }
    }

    // Push animated proxies into the InstancedMeshes (poolskaters)
    if (ad.instanced) syncInstancedGroups(ad.instanced);
  }
}

export function disposeFloraMesh(scene: THREE.Scene, fm: FloraMesh): void {
  scene.remove(fm.group);
  disposeObjectTree(fm.group);
}

// =============================================================================
// ROCK MESHES — static planet scenery
// =============================================================================

export interface RockMesh {
  group: THREE.Group;
  rockId: number;
  /** How it sits on the ground (groundSeat.ts); absent when placed the old way. */
  seat?: PropSeat;
}


export function createRockMesh(
  rock: RockState,
  worldPos: THREE.Vector3,
  surfaceNormal: THREE.Vector3,
  scene: THREE.Scene,
  planetRadius: number,
  opts: PropScaleOpts = {},
): RockMesh {
  if (rock.size === 'normal') {
    // a person-scale biome rock (decision 87): the planet's rock colour, sized from the person, culled by distance
    const ph = opts.personHeight ?? 0.055;
    // (66.9: the stands' variant and yaw, core's draws — the hull the cover slots read is the rock drawn, on either path)
    const m = buildRock(rock.rockType as RockKind, opts.rockColor ?? '#7a7468', rock.scale, treeProtoSeed(rock.rockType, rockVariant(rock.id)));
    m.rotation.y = rockYaw(rock.id);
    const extents = modelExtents(m);
    bakeStaticParts(m);
    const g = new THREE.Group();
    g.add(farEmpty(m, NORMAL_PROP_DRAW_PERSONS * ph));
    const scale = NORMAL_ROCK_PERSONS * ph;
    g.scale.setScalar(scale);
    const seat = placeProp(g, worldPos, surfaceNormal, opts.ground, { dir: rock.surfaceDir, kind: 'rock', half: extents.half * scale, height: extents.height * scale, seatHalf: opts.fine ? rockSeatHalf(rock, opts.fine) : 0 }, opts.planets, opts.fine);
    scene.add(g);
    return { group: g, rockId: rock.id, seat };
  }
  const model = buildRockModel(rock.rockType as 'basalt' | 'alabaster' | 'sandstone', rock.mode, rock.scale, rock.seed);
  const extents = modelExtents(model);
  bakeStaticParts(model); // rocks are fully static: ~8 meshes → 1-2

  const group = new THREE.Group();
  group.add(model);
  const scale = (ROCK_MODE_SCALES[rock.mode] || 3) * (surfacePropScale(planetRadius) / 2);
  group.scale.setScalar(scale);

  // On the ground's frame (the facet under a small rock, a fit across the footprint under a big one), sunk so no side floats
  const seat = placeProp(group, worldPos, surfaceNormal, opts.ground, { dir: rock.surfaceDir, kind: 'rock', half: extents.half * scale, height: extents.height * scale, seatHalf: opts.fine ? rockSeatHalf(rock, opts.fine) : 0 }, opts.planets, opts.fine);

  scene.add(group);

  return { group, rockId: rock.id, seat };
}

export function disposeRockMesh(scene: THREE.Scene, rm: RockMesh): void {
  scene.remove(rm.group);
  disposeObjectTree(rm.group);
}

// =============================================================================
// ENVIRONMENTAL EFFECT MESHES — driftwing, thermalcrawler, aurora, rings
// =============================================================================

export interface EnvironmentalEffectMesh {
  group: THREE.Group;
  effectId: number;
  effectType: EnvironmentalEffectType;
  animData: EffectAnimData;
  planetIndex: number;
}

export function createEnvironmentalEffectMesh(
  effect: EnvironmentalEffectState,
  planet: PlanetState,
  scene: THREE.Scene
): EnvironmentalEffectMesh {
  const { group: model, animData } = buildEnvironmentalEffect(effect.effectType);

  const group = new THREE.Group();
  group.add(model);

  if (effect.effectType === 'planetRings') {
    // Rings centered on planet, scaled by planet radius
    group.position.set(planet.position.x, planet.position.y, planet.position.z);
    const ringScale = planet.radius * 0.85;
    model.scale.setScalar(ringScale);
  } else if (effect.effectType === 'aurora') {
    // Aurora bands wrap the planet poles — center on planet, scale to actual radius
    group.position.set(planet.position.x, planet.position.y, planet.position.z);
    // Orient the aurora's pole axis along surfaceDir
    const normal = new THREE.Vector3(effect.surfaceDir.x, effect.surfaceDir.y, effect.surfaceDir.z);
    const up = new THREE.Vector3(0, 1, 0);
    group.quaternion.setFromUnitVectors(up, normal);
    // Model built at reference radius 1.85, scale to actual planet
    model.scale.setScalar(planet.radius / 1.85);
  } else {
    // Surface-based effects (driftwing, thermalcrawler) — sit on the actual terrain
    // (the bare sphere radius left crawlers hovering over craters), orient to surface
    const normal = new THREE.Vector3(effect.surfaceDir.x, effect.surfaceDir.y, effect.surfaceDir.z).normalize();
    const surfaceOffset = getSurfaceRadius(planet, effect.surfaceDir);
    group.position.set(
      planet.position.x + normal.x * surfaceOffset,
      planet.position.y + normal.y * surfaceOffset,
      planet.position.z + normal.z * surfaceOffset
    );
    const up = new THREE.Vector3(0, 1, 0);
    group.quaternion.setFromUnitVectors(up, normal);
    // Scale to planet-appropriate size (models built ~1 unit spread, planet radius 3-6)
    const surfaceScale = planet.radius * 0.4;
    model.scale.setScalar(surfaceScale);
  }

  scene.add(group);

  return { group, effectId: effect.id, effectType: effect.effectType, animData, planetIndex: effect.planetIndex };
}

export function updateEnvironmentalEffectMeshes(
  effectMeshes: EnvironmentalEffectMesh[],
  dt: number,
  time: number
): void {
  for (const em of effectMeshes) {
    const ad = em.animData;

    // --- Driftwing flock ---
    if (ad.boids) {
      for (const b of ad.boids) {
        if (ad.scattered && !b.scattered) {
          // Scatter outward
          const dir = b.mesh.position.clone().normalize();
          b.vel.set(
            dir.x * 2 + (Math.random() - 0.5) * 1.5,
            1.0 + Math.random() * 1.0,
            dir.z * 2 + (Math.random() - 0.5) * 1.5
          );
          b.scattered = true;
          b.scatterTime = time;
        }

        if (b.scattered && time - b.scatterTime > 2.0) {
          // Regroup toward home
          const toHome = b.home.clone().sub(b.mesh.position);
          b.vel.lerp(toHome.multiplyScalar(0.5), dt * 0.8);
          if (b.mesh.position.distanceTo(b.home) < 0.2) {
            b.scattered = false;
          }
        }

        if (!b.scattered && !ad.scattered) {
          // Gentle drift
          b.phase += dt * (0.3 + Math.random() * 0.05);
          const targetY = b.home.y + Math.sin(b.phase) * 0.1;
          b.vel.y += (targetY - b.mesh.position.y) * dt * 2;
          b.vel.x += (Math.sin(time * 0.5 + b.phase) * 0.08 - b.vel.x) * dt;
          b.vel.z += (Math.cos(time * 0.5 + b.phase) * 0.08 - b.vel.z) * dt;
          b.vel.multiplyScalar(0.97);
        }

        // Apply velocity
        b.mesh.position.addScaledVector(b.vel, dt);
        // Face direction of travel
        if (b.vel.length() > 0.01) {
          b.mesh.lookAt(b.mesh.position.clone().add(b.vel));
        }
        // Wing flutter
        b.mesh.rotation.z = Math.sin(time * 8 + b.phase) * (b.scattered ? 0.3 : 0.1);
      }

      // Auto-reset scatter after 4 seconds
      if (ad.scattered && ad.scatterStart !== undefined && time - ad.scatterStart > 4.0) {
        ad.scattered = false;
      }
    }

    // --- Thermalcrawler swarm ---
    if (ad.crawlers) {
      for (const c of ad.crawlers) {
        if (ad.disturbed && !c.retracted) {
          c.retracted = true;
          c.retractTime = time;
        }

        if (c.retracted) {
          const t = Math.min((time - c.retractTime) * 4, 1);
          c.bodyMesh.scale.y = 0.4 * (1 - t) + 0.1 * t;
          for (const leg of c.legs) {
            leg.visible = t < 0.5;
          }
          if (time - c.retractTime > 3.0) {
            c.retracted = false;
            ad.disturbed = false;
          }
        } else {
          // Restore shape
          c.bodyMesh.scale.y += (0.4 - c.bodyMesh.scale.y) * dt * 3;
          for (const leg of c.legs) leg.visible = true;
          // Wander
          c.phase += dt;
          c.heading += Math.sin(c.phase * 0.7) * 0.5 * dt;
          c.vel.x = Math.cos(c.heading) * 0.03;
          c.vel.z = Math.sin(c.heading) * 0.03;
          c.group.position.x += c.vel.x * dt;
          c.group.position.z += c.vel.z * dt;
          // Keep crawlers on the surface — clamp to wander radius
          const crawlDist = Math.sqrt(c.group.position.x ** 2 + c.group.position.z ** 2);
          if (crawlDist > 0.8) {
            c.heading = Math.atan2(-c.group.position.z, -c.group.position.x) + (Math.random() - 0.5) * 0.8;
            const norm = 0.8 / crawlDist;
            c.group.position.x *= norm;
            c.group.position.z *= norm;
          }
          c.group.rotation.y = c.heading;
          // Leg animation
          let legIdx = 0;
          for (const leg of c.legs) {
            leg.rotation.x = Math.sin(time * 3 + legIdx * 1.2 + c.phase) * 0.15;
            legIdx++;
          }
        }
      }
    }

    // Push animated proxies into the InstancedMeshes (boids, crawlers)
    if (ad.instanced) syncInstancedGroups(ad.instanced);

    // --- Aurora bands (one shared geometry; each band's run of it from `start`) ---
    if (ad.bands && ad.auroraGeo) {
      const pos = ad.auroraGeo.attributes.position.array as Float32Array;
      const col = ad.auroraGeo.attributes.color.array as Float32Array;
      for (const b of ad.bands) {
        const base = b.basePositions;
        const baseCol = b.baseColors;
        const segs = b.segs;
        const rows = b.vertRows;
        const at = b.start * 3;
        // the band's opacity pulse, folded into its colour (additive: the same light as a material opacity)
        const opacity = b.opacity * (0.7 + 0.3 * Math.sin(time * b.pulseSpeed + b.phase));

        for (let i = 0; i <= segs; i++) {
          const theta = (i / segs) * Math.PI * 2;
          const swirlAngle = theta + time * b.flowSpeed;
          const radialWave = Math.sin(swirlAngle * 3 + time * b.waveSpeed) * 0.08
                           + Math.sin(swirlAngle * 7 + time * b.waveSpeed * 1.7) * 0.03;
          const heightWave = Math.sin(swirlAngle * 4 + time * b.waveSpeed * 1.3 + b.phase) * 0.06;

          for (let j = 0; j <= rows; j++) {
            const idx = (i * (rows + 1) + j) * 3;
            const v = j / rows;
            const by = base[idx + 1];

            const dx = Math.cos(swirlAngle) * b.ringR;
            const dz = Math.sin(swirlAngle) * b.ringR;
            const vertStretch = (v - 0.5) * heightWave * (1 + Math.sin(swirlAngle * 5 + time * 0.3) * 0.5);

            pos[at + idx] = dx + Math.cos(swirlAngle) * radialWave * (1 - v * 0.5);
            pos[at + idx + 1] = by + vertStretch + heightWave * v;
            pos[at + idx + 2] = dz + Math.sin(swirlAngle) * radialWave * (1 - v * 0.5);

            const colorPulse = (0.6 + 0.4 * Math.sin(swirlAngle * 2 + time * b.pulseSpeed * 3 + v * 2)) * opacity;
            col[at + idx] = baseCol[idx] * colorPulse;
            col[at + idx + 1] = baseCol[idx + 1] * colorPulse;
            col[at + idx + 2] = baseCol[idx + 2] * colorPulse;
          }
        }
      }
      ad.auroraGeo.attributes.position.needsUpdate = true;
      ad.auroraGeo.attributes.color.needsUpdate = true;
      if (ad.northGlow) {
        ad.northGlow.intensity = 0.5 + Math.sin(time * 0.6) * 0.2;
      }
      if (ad.southGlow) {
        ad.southGlow.intensity = 0.25 + Math.sin(time * 0.5 + 1) * 0.1;
      }
    }

    // --- Planet rings shimmer ---
    if (ad.shimmerData && ad.shimmerParticles) {
      const posArr = ad.shimmerParticles.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < ad.shimmerData.length; i++) {
        const s = ad.shimmerData[i];
        s.angle += s.speed * dt;
        posArr[i * 3] = Math.cos(s.angle) * s.dist;
        posArr[i * 3 + 2] = Math.sin(s.angle) * s.dist;
      }
      ad.shimmerParticles.geometry.attributes.position.needsUpdate = true;
      (ad.shimmerParticles.material as THREE.PointsMaterial).opacity =
        0.25 + Math.sin(time * 2) * 0.06;
    }
  }
}

export function disposeEnvironmentalEffectMesh(
  scene: THREE.Scene,
  em: EnvironmentalEffectMesh
): void {
  scene.remove(em.group);
  disposeObjectTree(em.group);
}

/**
 * Trigger driftwing scatter on matching planet effect meshes.
 */
export function scatterDriftwings(
  effectMeshes: EnvironmentalEffectMesh[],
  planetIndex: number,
  time: number
): void {
  for (const em of effectMeshes) {
    if (em.effectType === 'driftwingFlock' && em.planetIndex === planetIndex) {
      if (em.animData.boids) {
        em.animData.scattered = true;
        em.animData.scatterStart = time;
      }
    }
  }
}
